import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  Matrix4,
  Mesh,
  ShapeGeometry,
  Vector3,
} from "three";

import type { BuildingRenderConfig } from "../../config/buildings.ts";
import {
  authoredBuildingPassagesForBuilding,
  BUILDING_PASSAGE_LINING_DEPTH_BELOW_BASE_M,
  BUILDING_PASSAGE_LINING_THICKNESS_M,
  type AuthoredBuildingPassageSpec,
} from "../../config/buildingPassages.ts";
import {
  OLD_TOWN_BALCONY_MINIMUM_FRONTAGE_M,
  OLD_TOWN_CHIMNEY_MAXIMUM_COUNT,
  OLD_TOWN_CHIMNEY_SPACING_M,
  OLD_TOWN_COURTYARD_DETAIL_MINIMUM_AREA_M2,
  OLD_TOWN_COURTYARD_DETAIL_MINIMUM_EDGE_M,
  OLD_TOWN_COURTYARD_EAVE_HEIGHT_M,
  OLD_TOWN_COURTYARD_FURNITURE_MINIMUM_EDGE_M,
  OLD_TOWN_COURTYARD_PLINTH_HEIGHT_M,
  OLD_TOWN_DETAIL_MAXIMUM_STREET_DISTANCE_M,
  OLD_TOWN_DETAIL_MINIMUM_FRONTAGE_M,
  OLD_TOWN_DOOR_MINIMUM_FRONTAGE_M,
  OLD_TOWN_GUTTER_DEPTH_M,
  OLD_TOWN_GUTTER_HEIGHT_M,
  OLD_TOWN_COMPLEX_ROOF_APRON_MAXIMUM_RUN_M,
  OLD_TOWN_COMPLEX_ROOF_APRON_MINIMUM_RUN_M,
  OLD_TOWN_ROOF_WINDOW_EDGE_CLEARANCE_M,
  OLD_TOWN_ROOF_WINDOW_FRAME_MARGIN_M,
  OLD_TOWN_ROOF_WINDOW_MAXIMUM_COUNT,
  OLD_TOWN_ROOF_WINDOW_SLOPE_HEIGHT_M,
  OLD_TOWN_ROOF_WINDOW_SLOPE_PROGRESS,
  OLD_TOWN_ROOF_WINDOW_SPACING_M,
  OLD_TOWN_ROOF_WINDOW_SURFACE_OFFSET_M,
  OLD_TOWN_ROOF_WINDOW_WIDTH_M,
  OLD_TOWN_WINDOW_FRAME_DEPTH_M,
  OLD_TOWN_WINDOW_MULLION_DEPTH_M,
  OLD_TOWN_WINDOW_PANEL_OFFSET_M,
  OLD_TOWN_WINDOW_SILL_DEPTH_M,
  OLD_TOWN_WINDOW_SURROUND_MAXIMUM_COLUMNS,
  OLD_TOWN_WINDOW_SURROUND_MINIMUM_FRONTAGE_M,
  oldTownCourtyardDetailProfileForBuilding,
  oldTownFacadeTreatmentForBuilding,
  oldTownStreetDetailProfileForBuilding,
  type OldTownDoorPanelPattern,
  type OldTownFacadeTreatment,
} from "../../config/oldTownBuildingArt.ts";
import { SOVIET_PANEL_DETAIL_CONFIG } from "../../config/sovietPanelBuildingArt.ts";
import {
  TOWN_HALL_SURROUNDINGS_FLOOR_HEIGHT_M,
  TOWN_HALL_SURROUNDINGS_REFERENCE_POINT,
  TOWN_HALL_SURROUNDINGS_ROOF_ALLOWANCE_M,
  townHallSurroundingReferenceForBuilding,
  type TownHallSurroundingReference,
} from "../../config/townHallSurroundings.ts";
import {
  ZVERYNAS_BACKGROUND_DETAIL_CONFIG,
} from "../../config/zverynas.ts";
import {
  SNIPISKES_BACKGROUND_DETAIL_CONFIG,
} from "../../config/snipiskes.ts";
import { GELEZINIO_VILKO_TUNNEL_ART } from "../../config/tunnelArt.ts";
import {
  generateVilniusBuildingVisualSpec,
  type OldTownBuildingVisualSpec,
  type SnipiskesBuildingVisualSpec,
  type SovietPanelBuildingVisualSpec,
  type VilniusBuildingStyleFamily,
  type VilniusBuildingVisualSpec,
  type ZverynasBuildingVisualSpec,
} from "../../world/buildings/index.ts";
import {
  buildingPassageSpanWithinFootprint,
  passagesOpeningFootprintRing,
  wallOpeningIntervalsForPassages,
} from "../../world/buildings/passages.ts";
import {
  isClosedRing,
  ringCentroid,
  signedRingAreaM2,
  type LinearRing,
  type PolygonWithHoles,
} from "../../world/geometry.ts";
import type {
  BuildingFootprintData,
  WorldTileId,
} from "../../world/schema/tile.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";
import { createPolygonShape } from "./developmentTileSetView.ts";

export interface StylizedBuildingBatchOptions {
  readonly tileId: WorldTileId | string;
  readonly sourceTileId?: WorldTileId | string;
  readonly tileOrigin: Readonly<{ x: number; z: number }>;
  readonly styleFamilyOverride?: VilniusBuildingStyleFamily;
  readonly heightScale?: number;
  readonly castShadow?: boolean;
  readonly includeOldTownDetails?: boolean;
  readonly frontageLines?: readonly Readonly<{
    points: readonly Readonly<{ x: number; z: number }>[];
  }>[];
}

export interface StylizedBuildingBatch {
  readonly object: Group;
  readonly visualSpecs: readonly VilniusBuildingVisualSpec[];
  readonly geometryCount: number;
  readonly triangleCount: number;
}

type TimberBuildingVisualSpec = Readonly<
  ZverynasBuildingVisualSpec | SnipiskesBuildingVisualSpec
>;

interface TimberBackgroundDetailConfig {
  readonly minimumFrontageM: number;
  readonly maximumStreetDistanceM: number;
  readonly maximumWindowColumns: number;
  readonly sidingCourseSpacingM: number;
  readonly minimumDoorClearanceM: number;
  readonly dormerSpacingM: number;
  readonly maximumDormerCount: number;
}

interface GeometryBucket {
  readonly material: ReturnType<CityMaterialLibrary["facade"]>;
  readonly positions: number[];
  readonly normals: number[];
  readonly uvs: number[];
  readonly indices: number[];
}

interface PreparedFrontageSegment {
  readonly from: Readonly<{ x: number; z: number }>;
  readonly to: Readonly<{ x: number; z: number }>;
  readonly minX: number;
  readonly minZ: number;
  readonly maxX: number;
  readonly maxZ: number;
}

const ORIENTED_BOX_TEMPLATE = (() => {
  const geometry = new BoxGeometry(1, 1, 1);
  const position = geometry.getAttribute("position");
  const normal = geometry.getAttribute("normal");
  const uv = geometry.getAttribute("uv");
  const index = geometry.index;
  if (normal === undefined || uv === undefined || index === null) {
    geometry.dispose();
    throw new Error("The oriented box template requires indexed box geometry.");
  }
  const result = {
    positions: Float32Array.from(
      { length: position.count * 3 },
      (_, component) => position.array[component] ?? 0,
    ),
    normals: Float32Array.from(
      { length: normal.count * 3 },
      (_, component) => normal.array[component] ?? 0,
    ),
    uvs: Float32Array.from(
      { length: uv.count * 2 },
      (_, component) => uv.array[component] ?? 0,
    ),
    indices: Uint16Array.from(
      { length: index.count },
      (_, component) => index.array[component] ?? 0,
    ),
  };
  geometry.dispose();
  return result;
})();

export function createStylizedBuildingBatch(
  buildings: readonly Readonly<BuildingFootprintData>[],
  materials: CityMaterialLibrary,
  config: Readonly<BuildingRenderConfig>,
  options: Readonly<StylizedBuildingBatchOptions>,
): StylizedBuildingBatch {
  const group = new Group();
  group.name = `runtime-buildings:${options.tileId}`;
  group.userData = {
    tileId: options.tileId,
    representation: "deterministic-vilnius-building-visual-spec",
    buildingIds: buildings.map((building) => building.id),
  };
  const wallBuckets = new Map<string, GeometryBucket>();
  const roofBuckets = new Map<string, GeometryBucket>();
  const visualSpecs: VilniusBuildingVisualSpec[] = [];
  const referenceTreatedBuildingIds: string[] = [];
  const oldTownDetailedBuildingIds: string[] = [];
  const gediminoAvenueDetailedBuildingIds: string[] = [];
  const zverynasDetailedBuildingIds: string[] = [];
  const snipiskesDetailedBuildingIds: string[] = [];
  const sovietPanelDetailedBuildingIds: string[] = [];
  const authoredBuildingPassageIds: string[] = [];
  group.userData.referenceTreatedBuildingIds = referenceTreatedBuildingIds;
  group.userData.oldTownDetailedBuildingIds = oldTownDetailedBuildingIds;
  group.userData.gediminoAvenueDetailedBuildingIds =
    gediminoAvenueDetailedBuildingIds;
  group.userData.zverynasDetailedBuildingIds = zverynasDetailedBuildingIds;
  group.userData.snipiskesDetailedBuildingIds = snipiskesDetailedBuildingIds;
  group.userData.sovietPanelDetailedBuildingIds =
    sovietPanelDetailedBuildingIds;
  group.userData.authoredBuildingPassageIds = authoredBuildingPassageIds;
  const frontageSegments = prepareFrontageSegments(
    options.frontageLines ?? [],
  );

  for (const building of buildings) {
    const passages = options.sourceTileId === undefined
      ? []
      : authoredBuildingPassagesForBuilding(
          options.sourceTileId,
          building.id,
        );
    authoredBuildingPassageIds.push(
      ...passages.map((passage) => passage.id),
    );
    const tileCentroid = ringCentroid(building.footprint.outer);
    const globalCentroid = {
      x: options.tileOrigin.x + tileCentroid.x,
      z: options.tileOrigin.z + tileCentroid.z,
    };
    const reference = townHallSurroundingReferenceForBuilding(building.id);
    const treatment = reference?.treatment ??
      oldTownFacadeTreatmentForBuilding(building.id, globalCentroid);
    const visualSpec = applyTownHallSurroundingReference(
      generateVilniusBuildingVisualSpec(
        building,
        {
          tile: tileCentroid,
          global: globalCentroid,
        },
        options.styleFamilyOverride === undefined
          ? {}
          : { styleFamilyOverride: options.styleFamilyOverride },
      ),
      reference,
    );
    visualSpecs.push(visualSpec);
    const heightScale = options.heightScale ?? 1;
    const roofProfile = roofProfileFor(
      building.footprint,
      visualSpec,
      config,
      heightScale,
    );
    const oldTownReferencePoint =
      treatment !== null &&
        visualSpec.styleFamily === "old_town" &&
        options.includeOldTownDetails !== false
        ? reference === null
          ? nearestFrontageReferencePoint(
              building.footprint,
              tileCentroid,
              frontageSegments,
            )
          : {
              x: TOWN_HALL_SURROUNDINGS_REFERENCE_POINT.x -
                options.tileOrigin.x,
              z: TOWN_HALL_SURROUNDINGS_REFERENCE_POINT.z -
                options.tileOrigin.z,
            }
        : null;
    const oldTownReferenceEdge = oldTownReferencePoint === null
      ? null
      : referenceFacadeEdge(building.footprint, oldTownReferencePoint);
    const authorsOldTownDetail =
      oldTownReferenceEdge !== null &&
      oldTownReferenceEdge.lengthM >= OLD_TOWN_DETAIL_MINIMUM_FRONTAGE_M;
    const authoredCourtyardHoleIndexes = authorsOldTownDetail
      ? new Set(
          building.footprint.holes
            .map((_hole, index) => index)
            .filter((index) =>
              qualifiesForOldTownCourtyardDetail(
                building.footprint.holes[index]!,
              )
            ),
        )
      : new Set<number>();
    const sovietPanelEdges =
      visualSpec.styleFamily === "soviet_panel" &&
        options.includeOldTownDetails !== false
        ? sovietPanelFacadeEdges(building.footprint)
        : null;
    const authorsSovietPanelFacade =
      sovietPanelEdges !== null && sovietPanelEdges.long.length > 0;
    const facadeMaterial = materials.facade(visualSpec);
    if (authorsSovietPanelFacade) {
      addGeometry(
        wallBuckets,
        `soviet-panel-detail-facade-base:${visualSpec.facade.palette.id}`,
        materials.blankFacade(visualSpec),
        createFacadeGeometry(
          building.footprint,
          building.baseElevationM,
          roofProfile.eaveElevationM,
          visualSpec,
          passages,
        ),
      );
    } else {
      addGeometry(
        wallBuckets,
        visualSpec.facade.palette.id,
        facadeMaterial,
        createFacadeGeometry(
          building.footprint,
          building.baseElevationM,
          roofProfile.eaveElevationM,
          visualSpec,
          passages,
          (holeIndex) =>
            holeIndex === null ||
            !authoredCourtyardHoleIndexes.has(holeIndex),
        ),
      );
      if (authoredCourtyardHoleIndexes.size > 0) {
        addGeometry(
          wallBuckets,
          `old-town-courtyard-base:${visualSpec.facade.palette.id}`,
          materials.blankFacade(visualSpec),
          createFacadeGeometry(
            building.footprint,
            building.baseElevationM,
            roofProfile.eaveElevationM,
            visualSpec,
            passages,
            (holeIndex) =>
              holeIndex !== null &&
              authoredCourtyardHoleIndexes.has(holeIndex),
          ),
        );
      }
    }
    if (
      (visualSpec.roof.type === "gable" ||
        visualSpec.roof.type === "mansard") &&
      roofProfile.frame !== null
    ) {
      addGeometry(
        wallBuckets,
        visualSpec.facade.palette.id,
        facadeMaterial,
        createPitchedRoofEndGeometry(
          roofProfile,
          visualSpec.roof.type,
        ),
      );
    }
    addBuildingPassageLiningGeometry(
      wallBuckets,
      materials,
      building.footprint,
      visualSpec,
      passages,
    );
    addGeometry(
      roofBuckets,
      visualSpec.roof.paletteId,
      materials.roof(visualSpec),
      createRoofGeometry(
        building.footprint,
        roofProfile,
        visualSpec,
      ),
    );
    if (
      treatment !== null &&
      visualSpec.styleFamily === "old_town" &&
      options.includeOldTownDetails !== false
    ) {
      const detailAdded = oldTownReferencePoint !== null &&
        addOldTownArchitecturalDetailGeometry(
          wallBuckets,
          roofBuckets,
          materials,
          building.footprint,
          visualSpec,
          roofProfile,
          treatment,
          oldTownReferencePoint,
        );
      if (detailAdded) {
        oldTownDetailedBuildingIds.push(building.id);
        if (treatment === "gedimino_avenue") {
          gediminoAvenueDetailedBuildingIds.push(building.id);
        }
        if (reference !== null) {
          referenceTreatedBuildingIds.push(building.id);
        }
      }
    }
    if (
      (
        visualSpec.styleFamily === "zverynas" ||
        visualSpec.styleFamily === "snipiskes"
      ) &&
      options.includeOldTownDetails !== false
    ) {
      const detailConfig = visualSpec.styleFamily === "snipiskes"
        ? SNIPISKES_BACKGROUND_DETAIL_CONFIG
        : ZVERYNAS_BACKGROUND_DETAIL_CONFIG;
      const referencePoint = nearestFrontageReferencePoint(
        building.footprint,
        tileCentroid,
        frontageSegments,
        detailConfig.maximumStreetDistanceM,
      );
      const detailAdded = referencePoint !== null &&
        addZverynasArchitecturalDetailGeometry(
          wallBuckets,
          roofBuckets,
          materials,
          building.footprint,
          visualSpec,
          roofProfile,
          referencePoint,
          detailConfig,
        );
      if (detailAdded) {
        if (visualSpec.styleFamily === "snipiskes") {
          snipiskesDetailedBuildingIds.push(building.id);
        } else {
          zverynasDetailedBuildingIds.push(building.id);
        }
      }
    }
    if (
      visualSpec.styleFamily === "soviet_panel" &&
      options.includeOldTownDetails !== false &&
      addSovietPanelArchitecturalDetailGeometry(
        wallBuckets,
        roofBuckets,
        materials,
        building.footprint,
        visualSpec,
        roofProfile,
      )
    ) {
      sovietPanelDetailedBuildingIds.push(building.id);
    }
  }

  for (const [key, bucket] of [
    ...wallBuckets.entries(),
    ...roofBuckets.entries(),
  ]) {
    const geometry = geometryFromBucket(bucket, `${group.name}:${key}`);
    const mesh = new Mesh(geometry, bucket.material);
    mesh.name = `${group.name}:${key}`;
    // Procedural window panes and their shallow trim are facade cards rather
    // than projecting architectural masses. Keep them out of shadow maps so
    // they cannot produce a second, displaced window silhouette on sunlit
    // walls across any detailed district.
    mesh.castShadow = (options.castShadow ?? true) && !key.includes("window");
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  let triangleCount = 0;
  group.traverse((object) => {
    if (!(object instanceof Mesh)) {
      return;
    }
    triangleCount += object.geometry.index === null
      ? object.geometry.getAttribute("position").count / 3
      : object.geometry.index.count / 3;
  });
  return {
    object: group,
    visualSpecs,
    geometryCount: group.children.length,
    triangleCount: Math.floor(triangleCount),
  };
}

function addGeometry(
  buckets: Map<string, GeometryBucket>,
  key: string,
  material: GeometryBucket["material"],
  geometry: BufferGeometry,
): void {
  const bucket = buckets.get(key) ?? {
    material,
    positions: [],
    normals: [],
    uvs: [],
    indices: [],
  };
  if (!buckets.has(key)) {
    buckets.set(key, bucket);
  }
  const position = geometry.getAttribute("position");
  const normal = geometry.getAttribute("normal");
  const uv = geometry.getAttribute("uv");
  if (normal === undefined || uv === undefined) {
    geometry.dispose();
    throw new Error(`Geometry bucket ${key} requires normals and UVs`);
  }
  const vertexOffset = bucket.positions.length / 3;
  const positionArray = (position as unknown as {
    readonly array: ArrayLike<number>;
  }).array;
  const normalArray = (normal as unknown as {
    readonly array: ArrayLike<number>;
  }).array;
  const uvArray = (uv as unknown as {
    readonly array: ArrayLike<number>;
  }).array;
  const positionTargetOffset = bucket.positions.length;
  const normalTargetOffset = bucket.normals.length;
  const uvTargetOffset = bucket.uvs.length;
  bucket.positions.length += position.count * 3;
  bucket.normals.length += normal.count * 3;
  bucket.uvs.length += uv.count * 2;
  for (let index = 0; index < position.count * 3; index += 1) {
    bucket.positions[positionTargetOffset + index] = positionArray[index] ?? 0;
  }
  for (let index = 0; index < normal.count * 3; index += 1) {
    bucket.normals[normalTargetOffset + index] = normalArray[index] ?? 0;
  }
  for (let index = 0; index < uv.count * 2; index += 1) {
    bucket.uvs[uvTargetOffset + index] = uvArray[index] ?? 0;
  }
  if (geometry.index === null) {
    const indexTargetOffset = bucket.indices.length;
    bucket.indices.length += position.count;
    for (let index = 0; index < position.count; index += 1) {
      bucket.indices[indexTargetOffset + index] = vertexOffset + index;
    }
  } else {
    const indexArray = geometry.index.array;
    const indexTargetOffset = bucket.indices.length;
    bucket.indices.length += geometry.index.count;
    for (let index = 0; index < geometry.index.count; index += 1) {
      bucket.indices[indexTargetOffset + index] =
        vertexOffset + (indexArray[index] ?? 0);
    }
  }
  geometry.dispose();
}

function geometryFromBucket(
  bucket: Readonly<GeometryBucket>,
  name: string,
): BufferGeometry {
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(bucket.positions, 3),
  );
  geometry.setAttribute(
    "normal",
    new Float32BufferAttribute(bucket.normals, 3),
  );
  geometry.setAttribute("uv", new Float32BufferAttribute(bucket.uvs, 2));
  geometry.setIndex(bucket.indices);
  geometry.name = name;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function applyTownHallSurroundingReference(
  spec: Readonly<VilniusBuildingVisualSpec>,
  reference: Readonly<TownHallSurroundingReference> | null,
): VilniusBuildingVisualSpec {
  if (reference === null || spec.styleFamily !== "old_town") {
    return spec;
  }
  const sourceFloorCount = reference.sourceFloorCount;
  const floors = sourceFloorCount === undefined
    ? spec.floors
    : {
        count: sourceFloorCount,
        confidence: "source" as const,
      };
  const height = sourceFloorCount === undefined
    ? spec.height
    : {
        metres:
          sourceFloorCount * TOWN_HALL_SURROUNDINGS_FLOOR_HEIGHT_M +
          TOWN_HALL_SURROUNDINGS_ROOF_ALLOWANCE_M,
        confidence: "derived_from_source_floor_count" as const,
      };
  const result: OldTownBuildingVisualSpec = {
    ...spec,
    height,
    floors,
    facade: {
      ...spec.facade,
      ...(reference.palette === undefined
        ? {}
        : { palette: { ...reference.palette } }),
      windowRhythm: {
        ...spec.facade.windowRhythm,
        rowCount: floors.count,
      },
    },
  };
  return result;
}

interface ReferenceFacadeEdge {
  readonly midpoint: Readonly<{ x: number; z: number }>;
  readonly along: Readonly<{ x: number; z: number }>;
  readonly outward: Readonly<{ x: number; z: number }>;
  readonly lengthM: number;
}

interface SovietPanelFacadeEdges {
  readonly all: readonly ReferenceFacadeEdge[];
  readonly long: readonly ReferenceFacadeEdge[];
  readonly ends: readonly ReferenceFacadeEdge[];
}

function addSovietPanelArchitecturalDetailGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  roofBuckets: Map<string, GeometryBucket>,
  materials: CityMaterialLibrary,
  footprint: Readonly<PolygonWithHoles>,
  spec: Readonly<SovietPanelBuildingVisualSpec>,
  roofProfile: Readonly<RoofProfile>,
): boolean {
  const edges = sovietPanelFacadeEdges(footprint);
  if (edges.long.length === 0) {
    return false;
  }
  const facadeHeightM = roofProfile.eaveElevationM - spec.baseElevationM;
  const rowCount = Math.max(1, spec.floors.count);
  const floorHeightM = facadeHeightM / rowCount;
  if (facadeHeightM < 5 || floorHeightM < 1.8) {
    return false;
  }

  const trimMaterial = materials.architecturalTrim(spec);
  const trimKey = `soviet-panel-detail-trim:${spec.facade.palette.id}`;
  const jointMaterial = materials.panelJoint(spec);
  const jointKey = `soviet-panel-detail-joint:${spec.facade.palette.id}`;
  const plinthKey = "soviet-panel-detail-plinth";
  const doorKey = "soviet-panel-detail-entrance";
  const apronKey = "soviet-panel-detail-facade-apron";
  const approachKey = "soviet-panel-detail-entrance-approach";
  const windowKey = "soviet-panel-detail-loggia-window";
  const roofKey = `soviet-panel-detail-roof:${spec.roof.paletteId}`;
  const roofHousingKey =
    `soviet-panel-detail-roof-housing:${spec.facade.palette.id}`;
  const metalKey = "soviet-panel-detail-metal";

  for (const edge of edges.long) {
    addGeometry(
      wallBuckets,
      apronKey,
      materials.streetSurfaces.general.pedestrian,
      createOrientedBoxGeometry(
        edge,
        0,
        edge.lengthM,
        spec.baseElevationM +
          SOVIET_PANEL_DETAIL_CONFIG.facadeApronHeightM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.facadeApronHeightM,
        SOVIET_PANEL_DETAIL_CONFIG.facadeApronDepthM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.facadeApronDepthM,
      ),
    );
    addGeometry(
      wallBuckets,
      plinthKey,
      materials.buildingPassageLining,
      createOrientedBoxGeometry(
        edge,
        0,
        edge.lengthM,
        spec.baseElevationM + SOVIET_PANEL_DETAIL_CONFIG.plinthHeightM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.plinthHeightM,
        SOVIET_PANEL_DETAIL_CONFIG.plinthDepthM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.plinthDepthM,
      ),
    );
    for (let row = 1; row < rowCount; row += 1) {
      addGeometry(
        wallBuckets,
        jointKey,
        jointMaterial,
        createOrientedBoxGeometry(
          edge,
          0,
          edge.lengthM,
          spec.baseElevationM + floorHeightM * row,
          SOVIET_PANEL_DETAIL_CONFIG.floorJointHeightM,
          SOVIET_PANEL_DETAIL_CONFIG.floorJointDepthM / 2,
          SOVIET_PANEL_DETAIL_CONFIG.floorJointDepthM,
        ),
      );
    }

    const bayCount = clampInteger(
      edge.lengthM /
        Math.max(2.6, edge.lengthM / Math.max(1, spec.facade.bayCount)),
      3,
      24,
    );
    const bayWidthM = edge.lengthM / bayCount;
    for (let column = 1; column < bayCount; column += 1) {
      addGeometry(
        wallBuckets,
        jointKey,
        jointMaterial,
        createOrientedBoxGeometry(
          edge,
          -edge.lengthM / 2 + bayWidthM * column,
          SOVIET_PANEL_DETAIL_CONFIG.panelJointWidthM,
          spec.baseElevationM + facadeHeightM / 2,
          facadeHeightM,
          SOVIET_PANEL_DETAIL_CONFIG.panelJointDepthM / 2,
          SOVIET_PANEL_DETAIL_CONFIG.panelJointDepthM,
        ),
      );
    }
    const entranceCount = clampInteger(
      edge.lengthM / SOVIET_PANEL_DETAIL_CONFIG.entranceSpacingM,
      1,
      SOVIET_PANEL_DETAIL_CONFIG.maximumEntranceCount,
    );
    const entranceOffsetsM: number[] = [];
    for (let entrance = 0; entrance < entranceCount; entrance += 1) {
      const alongOffsetM =
        -edge.lengthM / 2 + edge.lengthM * (entrance + 0.5) / entranceCount;
      entranceOffsetsM.push(alongOffsetM);
      addSovietPanelEntranceGeometry(
        wallBuckets,
        trimKey,
        doorKey,
        approachKey,
        windowKey,
        metalKey,
        trimMaterial,
        materials,
        edge,
        spec,
        alongOffsetM,
        floorHeightM,
        rowCount,
      );
    }

    const balconyColumns: number[] = [];
    for (let column = 0; column < bayCount; column += 1) {
      if (
        (column + (spec.seed & 1)) %
          spec.facade.windowRhythm.loggiaEveryBays === 0
      ) {
        balconyColumns.push(column);
      }
    }
    const selectedBalconyColumns = evenlySampleIndices(
      balconyColumns,
      SOVIET_PANEL_DETAIL_CONFIG.maximumBalconyColumnsPerFacade,
    );
    addSovietPanelRegularWindowGeometry(
      wallBuckets,
      windowKey,
      materials,
      edge,
      spec,
      bayCount,
      rowCount,
      floorHeightM,
      entranceOffsetsM,
      selectedBalconyColumns,
    );
    for (const column of selectedBalconyColumns) {
      const alongOffsetM =
        -edge.lengthM / 2 + bayWidthM * (column + 0.5);
      const balconyWidthM = Math.min(
        SOVIET_PANEL_DETAIL_CONFIG.balconyMaximumWidthM,
        Math.max(
          SOVIET_PANEL_DETAIL_CONFIG.balconyMinimumWidthM,
          bayWidthM * SOVIET_PANEL_DETAIL_CONFIG.balconyWidthToBayRatio,
        ),
      );
      for (let row = 1; row < rowCount; row += 1) {
        addSovietPanelBalconyGeometry(
          wallBuckets,
          trimKey,
          windowKey,
          metalKey,
          trimMaterial,
          materials,
          edge,
          spec,
          alongOffsetM,
          balconyWidthM,
          spec.baseElevationM + row * floorHeightM,
          floorHeightM,
        );
      }
    }
  }

  for (const edge of edges.ends) {
    for (let row = 1; row < rowCount; row += 1) {
      addGeometry(
        wallBuckets,
        plinthKey,
        materials.buildingPassageLining,
        createOrientedBoxGeometry(
          edge,
          0,
          edge.lengthM,
          spec.baseElevationM + floorHeightM * row,
          0.035,
          0.1,
          0.055,
        ),
      );
    }
  }

  for (const edge of edges.all) {
    addGeometry(
      roofBuckets,
      roofKey,
      materials.roof(spec),
      createOrientedBoxGeometry(
        edge,
        0,
        edge.lengthM,
        roofProfile.topElevationM +
          SOVIET_PANEL_DETAIL_CONFIG.parapetHeightM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.parapetHeightM,
        -SOVIET_PANEL_DETAIL_CONFIG.parapetDepthM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.parapetDepthM,
      ),
    );
  }
  addSovietPanelRoofEquipmentGeometry(
    roofBuckets,
    roofHousingKey,
    metalKey,
    trimMaterial,
    materials,
    edges.long[0]!,
    footprint,
    spec,
    roofProfile.topElevationM,
  );
  return true;
}

function addSovietPanelRegularWindowGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  windowKey: string,
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  spec: Readonly<SovietPanelBuildingVisualSpec>,
  bayCount: number,
  rowCount: number,
  floorHeightM: number,
  entranceOffsetsM: readonly number[],
  balconyColumns: readonly number[],
): void {
  const bayWidthM = edge.lengthM / bayCount;
  const windowWidthM = Math.min(
    bayWidthM - 0.36,
    Math.max(
      0.72,
      bayWidthM * spec.facade.windowRhythm.windowWidthToBayRatio,
    ),
  );
  const windowHeightM = Math.min(
    floorHeightM - 0.5,
    floorHeightM * spec.facade.windowRhythm.windowHeightToFloorRatio,
  );
  const balconyColumnSet = new Set(balconyColumns);
  const placements: FacadeWindowPlacement[] = [];
  for (let column = 0; column < bayCount; column += 1) {
    const alongOffsetM =
      -edge.lengthM / 2 + bayWidthM * (column + 0.5);
    const overlapsStairwell = entranceOffsetsM.some((entranceOffsetM) =>
      Math.abs(entranceOffsetM - alongOffsetM) <=
        (SOVIET_PANEL_DETAIL_CONFIG.stairwellBandWidthM + windowWidthM) / 2 +
          0.08
    );
    if (overlapsStairwell) {
      continue;
    }
    for (let row = 0; row < rowCount; row += 1) {
      if (row > 0 && balconyColumnSet.has(column)) {
        continue;
      }
      const placement = fitAttachedFacadeWindowPlacement({
        alongOffsetM,
        centerY: spec.baseElevationM + floorHeightM * (row + 0.52),
        widthM: windowWidthM,
        heightM: windowHeightM,
        litAtNight: false,
      }, {
        edgeLengthM: edge.lengthM,
        baseElevationM: spec.baseElevationM,
        topElevationM: spec.baseElevationM + floorHeightM * rowCount,
        horizontalAdornmentM: 0.08,
        bottomAdornmentM: 0.08,
        topAdornmentM: 0.08,
        minimumPanelWidthM: 0.44,
        minimumPanelHeightM: 0.56,
      });
      if (placement === null) {
        continue;
      }
      placements.push(placement);
    }
  }
  if (placements.length > 0) {
    addGeometry(
      wallBuckets,
      windowKey,
      materials.panelWindowUnlit,
      createFacadeQuadBatchGeometry(
        edge,
        placements.map((placement) => ({
          alongMinimumM:
            placement.alongOffsetM - placement.widthM / 2,
          alongMaximumM:
            placement.alongOffsetM + placement.widthM / 2,
          bottomElevationM:
            placement.centerY - placement.heightM / 2,
          topElevationM:
            placement.centerY + placement.heightM / 2,
          outwardOffsetM: 0.061,
        })),
      ),
    );
  }
}

function addSovietPanelEntranceGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  trimKey: string,
  doorKey: string,
  approachKey: string,
  windowKey: string,
  metalKey: string,
  trimMaterial: GeometryBucket["material"],
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  spec: Readonly<SovietPanelBuildingVisualSpec>,
  alongOffsetM: number,
  floorHeightM: number,
  rowCount: number,
): void {
  const stairwellHeightM = Math.max(
    0,
    floorHeightM * rowCount - SOVIET_PANEL_DETAIL_CONFIG.plinthHeightM,
  );
  if (stairwellHeightM > 0) {
    addGeometry(
      wallBuckets,
      trimKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM,
        SOVIET_PANEL_DETAIL_CONFIG.stairwellBandWidthM,
        spec.baseElevationM +
          SOVIET_PANEL_DETAIL_CONFIG.plinthHeightM + stairwellHeightM / 2,
        stairwellHeightM,
        SOVIET_PANEL_DETAIL_CONFIG.stairwellBandDepthM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.stairwellBandDepthM,
      ),
    );
  }
  addGeometry(
    wallBuckets,
    doorKey,
    materials.oldTownDoorLeaves.painted,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      SOVIET_PANEL_DETAIL_CONFIG.entranceWidthM,
      spec.baseElevationM + SOVIET_PANEL_DETAIL_CONFIG.entranceHeightM / 2,
      SOVIET_PANEL_DETAIL_CONFIG.entranceHeightM,
      SOVIET_PANEL_DETAIL_CONFIG.entrancePanelOffsetM,
      0.1,
    ),
  );
  for (const side of [-1, 1] as const) {
    addGeometry(
      wallBuckets,
      trimKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM +
          side *
            (SOVIET_PANEL_DETAIL_CONFIG.entranceWidthM / 2 +
              SOVIET_PANEL_DETAIL_CONFIG.entranceRevealWidthM / 2),
        SOVIET_PANEL_DETAIL_CONFIG.entranceRevealWidthM,
        spec.baseElevationM +
          SOVIET_PANEL_DETAIL_CONFIG.entranceHeightM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.entranceHeightM,
        SOVIET_PANEL_DETAIL_CONFIG.entranceRevealDepthM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.entranceRevealDepthM,
      ),
    );
  }
  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      SOVIET_PANEL_DETAIL_CONFIG.canopyWidthM,
      spec.baseElevationM + SOVIET_PANEL_DETAIL_CONFIG.entranceHeightM + 0.18,
      SOVIET_PANEL_DETAIL_CONFIG.canopyHeightM,
      SOVIET_PANEL_DETAIL_CONFIG.canopyDepthM / 2,
      SOVIET_PANEL_DETAIL_CONFIG.canopyDepthM,
    ),
  );
  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      SOVIET_PANEL_DETAIL_CONFIG.entranceLandingWidthM,
      spec.baseElevationM +
        SOVIET_PANEL_DETAIL_CONFIG.entranceLandingHeightM / 2,
      SOVIET_PANEL_DETAIL_CONFIG.entranceLandingHeightM,
      SOVIET_PANEL_DETAIL_CONFIG.entranceLandingDepthM / 2,
      SOVIET_PANEL_DETAIL_CONFIG.entranceLandingDepthM,
    ),
  );
  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      SOVIET_PANEL_DETAIL_CONFIG.entranceLandingWidthM + 0.28,
      spec.baseElevationM +
        SOVIET_PANEL_DETAIL_CONFIG.entranceStepHeightM / 2,
      SOVIET_PANEL_DETAIL_CONFIG.entranceStepHeightM,
      SOVIET_PANEL_DETAIL_CONFIG.entranceLandingDepthM +
        SOVIET_PANEL_DETAIL_CONFIG.entranceStepDepthM / 2,
      SOVIET_PANEL_DETAIL_CONFIG.entranceStepDepthM,
    ),
  );
  const entranceHardscapeDepthM =
    SOVIET_PANEL_DETAIL_CONFIG.entranceLandingDepthM +
    SOVIET_PANEL_DETAIL_CONFIG.entranceStepDepthM;
  addGeometry(
    wallBuckets,
    approachKey,
    materials.streetSurfaces.general.pedestrian,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      SOVIET_PANEL_DETAIL_CONFIG.entranceApproachWidthM,
      spec.baseElevationM +
        SOVIET_PANEL_DETAIL_CONFIG.entranceApproachHeightM / 2,
      SOVIET_PANEL_DETAIL_CONFIG.entranceApproachHeightM,
      entranceHardscapeDepthM +
        SOVIET_PANEL_DETAIL_CONFIG.entranceApproachLengthM / 2,
      SOVIET_PANEL_DETAIL_CONFIG.entranceApproachLengthM,
    ),
  );
  for (const side of [-1, 1] as const) {
    const railAlongOffsetM = alongOffsetM + side *
      (SOVIET_PANEL_DETAIL_CONFIG.entranceLandingWidthM / 2 - 0.08);
    for (const outwardOffsetM of [0.28, entranceHardscapeDepthM - 0.1]) {
      addGeometry(
        wallBuckets,
        metalKey,
        materials.streetPole,
        createOrientedBoxGeometry(
          edge,
          railAlongOffsetM,
          SOVIET_PANEL_DETAIL_CONFIG.entranceHandrailThicknessM,
          spec.baseElevationM +
            SOVIET_PANEL_DETAIL_CONFIG.entranceHandrailHeightM / 2,
          SOVIET_PANEL_DETAIL_CONFIG.entranceHandrailHeightM,
          outwardOffsetM,
          SOVIET_PANEL_DETAIL_CONFIG.entranceHandrailThicknessM,
        ),
      );
    }
    addGeometry(
      wallBuckets,
      metalKey,
      materials.streetPole,
      createOrientedBoxGeometry(
        edge,
        railAlongOffsetM,
        SOVIET_PANEL_DETAIL_CONFIG.entranceHandrailThicknessM,
        spec.baseElevationM +
          SOVIET_PANEL_DETAIL_CONFIG.entranceHandrailHeightM,
        SOVIET_PANEL_DETAIL_CONFIG.entranceHandrailThicknessM,
        entranceHardscapeDepthM / 2,
        entranceHardscapeDepthM - 0.38,
      ),
    );
  }
  const facadeTopElevationM =
    spec.baseElevationM + floorHeightM * rowCount;
  const entranceLight = fitAttachedFacadeWindowPlacement({
    alongOffsetM,
    centerY:
      spec.baseElevationM +
      SOVIET_PANEL_DETAIL_CONFIG.entranceHeightM + 0.04,
    widthM: SOVIET_PANEL_DETAIL_CONFIG.entranceLightWidthM,
    heightM: SOVIET_PANEL_DETAIL_CONFIG.entranceLightHeightM,
    litAtNight: true,
  }, {
    edgeLengthM: edge.lengthM,
    baseElevationM: spec.baseElevationM,
    topElevationM: facadeTopElevationM,
    horizontalAdornmentM: 0.05,
    bottomAdornmentM: 0.05,
    topAdornmentM: 0.05,
    minimumPanelWidthM: 0.24,
    minimumPanelHeightM: 0.18,
  });
  if (entranceLight !== null) {
    addGeometry(
      wallBuckets,
      windowKey,
      materials.panelWindowUnlit,
      createOrientedBoxGeometry(
        edge,
        entranceLight.alongOffsetM,
        entranceLight.widthM,
        entranceLight.centerY,
        entranceLight.heightM,
        SOVIET_PANEL_DETAIL_CONFIG.canopyDepthM + 0.025,
        0.05,
      ),
    );
  }
  for (let row = 1; row < rowCount; row += 1) {
    const stairwellWindow = fitAttachedFacadeWindowPlacement({
      alongOffsetM,
      centerY: spec.baseElevationM + floorHeightM * (row + 0.52),
      widthM: SOVIET_PANEL_DETAIL_CONFIG.stairwellWindowWidthM,
      heightM: Math.min(1.05, floorHeightM * 0.42),
      litAtNight: false,
    }, {
      edgeLengthM: edge.lengthM,
      baseElevationM: spec.baseElevationM,
      topElevationM: facadeTopElevationM,
      horizontalAdornmentM: 0.08,
      bottomAdornmentM: 0.08,
      topAdornmentM: 0.08,
      minimumPanelWidthM: 0.36,
      minimumPanelHeightM: 0.36,
    });
    if (stairwellWindow !== null) {
      addGeometry(
        wallBuckets,
        windowKey,
        materials.panelWindowUnlit,
        createOrientedBoxGeometry(
          edge,
          stairwellWindow.alongOffsetM,
          stairwellWindow.widthM,
          stairwellWindow.centerY,
          stairwellWindow.heightM,
          0.13,
          0.075,
        ),
      );
    }
  }
}

function addSovietPanelBalconyGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  trimKey: string,
  windowKey: string,
  metalKey: string,
  trimMaterial: GeometryBucket["material"],
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  spec: Readonly<SovietPanelBuildingVisualSpec>,
  alongOffsetM: number,
  widthM: number,
  floorBaseM: number,
  floorHeightM: number,
): void {
  const availableHalfWidthM =
    edge.lengthM / 2 - Math.abs(alongOffsetM) - 0.12;
  const attachedWidthM = Math.min(widthM, availableHalfWidthM * 2);
  if (attachedWidthM < 0.72) {
    return;
  }
  if (spec.facade.balconyTreatment === "projecting_solid") {
    const balconyWindow = fitAttachedFacadeWindowPlacement({
      alongOffsetM,
      centerY: floorBaseM + floorHeightM * 0.54,
      widthM: attachedWidthM * 0.72,
      heightM: floorHeightM * 0.64,
      litAtNight: false,
    }, {
      edgeLengthM: edge.lengthM,
      baseElevationM: floorBaseM,
      topElevationM: floorBaseM + floorHeightM,
      horizontalAdornmentM: 0.06,
      bottomAdornmentM: 0.06,
      topAdornmentM: 0.06,
      minimumPanelWidthM: 0.44,
      minimumPanelHeightM: 0.56,
    });
    if (balconyWindow !== null) {
      addGeometry(
        wallBuckets,
        windowKey,
        materials.panelWindowUnlit,
        createOrientedBoxGeometry(
          edge,
          balconyWindow.alongOffsetM,
          balconyWindow.widthM,
          balconyWindow.centerY,
          balconyWindow.heightM,
          0.13,
          0.065,
        ),
      );
    }
    addGeometry(
      wallBuckets,
      trimKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM,
        attachedWidthM,
        floorBaseM + SOVIET_PANEL_DETAIL_CONFIG.balconySlabHeightM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.balconySlabHeightM,
        SOVIET_PANEL_DETAIL_CONFIG.balconyDepthM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.balconyDepthM,
      ),
    );
    addGeometry(
      wallBuckets,
      trimKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM,
        attachedWidthM,
        floorBaseM + SOVIET_PANEL_DETAIL_CONFIG.balconyFrontHeightM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.balconyFrontHeightM,
        SOVIET_PANEL_DETAIL_CONFIG.balconyDepthM,
        0.12,
      ),
    );
    for (const side of [-1, 1] as const) {
      addGeometry(
        wallBuckets,
        trimKey,
        trimMaterial,
        createOrientedBoxGeometry(
          edge,
          alongOffsetM + side * attachedWidthM / 2,
          0.1,
          floorBaseM + SOVIET_PANEL_DETAIL_CONFIG.balconyFrontHeightM / 2,
          SOVIET_PANEL_DETAIL_CONFIG.balconyFrontHeightM,
          SOVIET_PANEL_DETAIL_CONFIG.balconyDepthM / 2,
          SOVIET_PANEL_DETAIL_CONFIG.balconyDepthM,
        ),
      );
    }
    return;
  }

  const loggiaWindow = fitAttachedFacadeWindowPlacement({
    alongOffsetM,
    centerY: floorBaseM + floorHeightM * 0.55,
    widthM: attachedWidthM,
    heightM: floorHeightM * 0.7,
    litAtNight: false,
  }, {
    edgeLengthM: edge.lengthM,
    baseElevationM: floorBaseM,
    topElevationM: floorBaseM + floorHeightM,
    horizontalAdornmentM: 0.08,
    bottomAdornmentM: 0.08,
    topAdornmentM: 0.08,
    minimumPanelWidthM: 0.44,
    minimumPanelHeightM: 0.56,
  });
  if (loggiaWindow !== null) {
    addGeometry(
      wallBuckets,
      windowKey,
      materials.panelWindowUnlit,
      createOrientedBoxGeometry(
        edge,
        loggiaWindow.alongOffsetM,
        loggiaWindow.widthM,
        loggiaWindow.centerY,
        loggiaWindow.heightM,
        SOVIET_PANEL_DETAIL_CONFIG.loggiaPanelOffsetM,
        SOVIET_PANEL_DETAIL_CONFIG.loggiaPanelDepthM,
      ),
    );
  }
  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      attachedWidthM + 0.18,
      floorBaseM + 0.39,
      spec.facade.balconyTreatment === "recessed_loggia" ? 0.74 : 0.38,
      SOVIET_PANEL_DETAIL_CONFIG.loggiaPanelOffsetM + 0.04,
      0.1,
    ),
  );
  for (const side of [-1, 1] as const) {
    addGeometry(
      wallBuckets,
      trimKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM + side * attachedWidthM / 2,
        0.1,
        floorBaseM + floorHeightM * 0.52,
        floorHeightM * 0.78,
        SOVIET_PANEL_DETAIL_CONFIG.loggiaSideDepthM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.loggiaSideDepthM,
      ),
    );
  }
  if (spec.facade.balconyTreatment === "enclosed_loggia") {
    addGeometry(
      wallBuckets,
      metalKey,
      materials.streetPole,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM,
        0.055,
        floorBaseM + floorHeightM * 0.57,
        floorHeightM * 0.62,
        SOVIET_PANEL_DETAIL_CONFIG.loggiaPanelOffsetM + 0.08,
        0.055,
      ),
    );
  }
}

function addSovietPanelRoofEquipmentGeometry(
  roofBuckets: Map<string, GeometryBucket>,
  roofHousingKey: string,
  metalKey: string,
  trimMaterial: GeometryBucket["material"],
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  footprint: Readonly<PolygonWithHoles>,
  spec: Readonly<SovietPanelBuildingVisualSpec>,
  roofElevationM: number,
): void {
  const depthM = footprintDepthForEdge(footprint, edge);
  const inwardOffsetM = -Math.max(
    SOVIET_PANEL_DETAIL_CONFIG.roofHousingMinimumInsetM,
    Math.min(depthM * 0.46, depthM - 1.2),
  );
  const housingCount = Math.min(
    SOVIET_PANEL_DETAIL_CONFIG.maximumRoofHousingCount,
    edge.lengthM >= 46 ? 2 : 1,
  );
  for (let index = 0; index < housingCount; index += 1) {
    const alongOffsetM = housingCount === 1
      ? 0
      : (index === 0 ? -1 : 1) * edge.lengthM * 0.22;
    addGeometry(
      roofBuckets,
      roofHousingKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM,
        SOVIET_PANEL_DETAIL_CONFIG.roofHousingWidthM,
        roofElevationM + SOVIET_PANEL_DETAIL_CONFIG.roofHousingHeightM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.roofHousingHeightM,
        inwardOffsetM,
        Math.min(
          SOVIET_PANEL_DETAIL_CONFIG.roofHousingDepthM,
          Math.max(1.2, depthM * 0.38),
        ),
      ),
    );
  }
  const ventCount = Math.min(
    SOVIET_PANEL_DETAIL_CONFIG.maximumRoofVentCount,
    1 + (spec.seed % SOVIET_PANEL_DETAIL_CONFIG.maximumRoofVentCount),
  );
  for (let index = 0; index < ventCount; index += 1) {
    const phase = (index + 1) / (ventCount + 1);
    addGeometry(
      roofBuckets,
      metalKey,
      materials.streetPole,
      createOrientedBoxGeometry(
        edge,
        -edge.lengthM * 0.32 + edge.lengthM * 0.64 * phase,
        SOVIET_PANEL_DETAIL_CONFIG.roofVentSizeM,
        roofElevationM + SOVIET_PANEL_DETAIL_CONFIG.roofVentHeightM / 2,
        SOVIET_PANEL_DETAIL_CONFIG.roofVentHeightM,
        inwardOffsetM - 0.8,
        SOVIET_PANEL_DETAIL_CONFIG.roofVentSizeM,
      ),
    );
  }
}

function sovietPanelFacadeEdges(
  footprint: Readonly<PolygonWithHoles>,
): SovietPanelFacadeEdges {
  const ring = normalizedWallRing(footprint.outer, false);
  const all: ReferenceFacadeEdge[] = [];
  for (let index = 1; index < ring.length; index += 1) {
    const from = ring[index - 1];
    const to = ring[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM < 3) {
      continue;
    }
    const along = { x: deltaX / lengthM, z: deltaZ / lengthM };
    all.push({
      midpoint: { x: (from.x + to.x) / 2, z: (from.z + to.z) / 2 },
      along,
      outward: { x: along.z, z: -along.x },
      lengthM,
    });
  }
  const maximumLengthM = all.reduce(
    (maximum, edge) => Math.max(maximum, edge.lengthM),
    0,
  );
  const long = all
    .filter((edge) =>
      edge.lengthM >= SOVIET_PANEL_DETAIL_CONFIG.minimumLongFacadeM &&
      edge.lengthM >=
        maximumLengthM * SOVIET_PANEL_DETAIL_CONFIG.longFacadeRatio
    )
    .sort((first, second) => second.lengthM - first.lengthM)
    .slice(0, 2);
  const longSet = new Set(long);
  return {
    all,
    long,
    ends: all.filter((edge) => !longSet.has(edge)).slice(0, 4),
  };
}

function footprintDepthForEdge(
  footprint: Readonly<PolygonWithHoles>,
  edge: Readonly<ReferenceFacadeEdge>,
): number {
  const frontProjection =
    edge.midpoint.x * edge.outward.x + edge.midpoint.z * edge.outward.z;
  let rearProjection = frontProjection;
  for (const point of footprint.outer) {
    rearProjection = Math.min(
      rearProjection,
      point.x * edge.outward.x + point.z * edge.outward.z,
    );
  }
  return Math.max(2.4, frontProjection - rearProjection);
}

function evenlySampleIndices(
  values: readonly number[],
  maximumCount: number,
): readonly number[] {
  if (values.length <= maximumCount) {
    return values;
  }
  const sampled: number[] = [];
  for (let index = 0; index < maximumCount; index += 1) {
    const sourceIndex = Math.round(
      index * (values.length - 1) / Math.max(1, maximumCount - 1),
    );
    const value = values[sourceIndex];
    if (value !== undefined && sampled.at(-1) !== value) {
      sampled.push(value);
    }
  }
  return sampled;
}

function addZverynasArchitecturalDetailGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  roofBuckets: Map<string, GeometryBucket>,
  materials: CityMaterialLibrary,
  footprint: Readonly<PolygonWithHoles>,
  spec: TimberBuildingVisualSpec,
  roofProfile: Readonly<RoofProfile>,
  referencePoint: Readonly<{ x: number; z: number }>,
  detailConfig: TimberBackgroundDetailConfig,
): boolean {
  const edge = referenceFacadeEdge(footprint, referencePoint);
  if (
    edge === null ||
    edge.lengthM < detailConfig.minimumFrontageM
  ) {
    return false;
  }
  const generatedRoofRiseM =
    roofProfile.topElevationM - roofProfile.eaveElevationM;
  const fallbackRoofRiseM = Math.min(
    3,
    Math.max(
      1.4,
      (roofProfile.topElevationM - spec.baseElevationM) * 0.24,
    ),
  );
  const eaveElevationM = roofProfile.topElevationM -
    (generatedRoofRiseM > 0.01
      ? generatedRoofRiseM
      : fallbackRoofRiseM);
  const roofRiseM = roofProfile.topElevationM - eaveElevationM;
  const facadeHeightM = eaveElevationM - spec.baseElevationM;
  if (facadeHeightM < 1.8) {
    return false;
  }

  const trimMaterial = materials.architecturalTrim(spec);
  const detailPrefix = spec.styleFamily === "snipiskes"
    ? "snipiskes"
    : "zverynas";
  const facadeMaskKey =
    `${detailPrefix}-detail-facade-mask:${spec.facade.palette.id}`;
  const trimKey = `${detailPrefix}-detail-trim:${spec.facade.palette.id}`;
  const windowTrimKey =
    `${detailPrefix}-detail-window-trim:${spec.facade.palette.id}`;
  const sidingKey = `${detailPrefix}-detail-siding:${spec.facade.palette.id}`;
  const litWindowKey = `${detailPrefix}-detail-window-lit`;
  const unlitWindowKey = `${detailPrefix}-detail-window-unlit`;
  const doorKey = `${detailPrefix}-detail-door`;
  const metalKey = `${detailPrefix}-detail-metal`;
  const roofKey = `${detailPrefix}-detail-roof:${spec.roof.paletteId}`;
  const chimneyKey = `${detailPrefix}-detail-chimney:${spec.facade.palette.id}`;
  const phase = ((spec.seed >>> 8) & 0xffff) / 0xffff;
  const doorTravelM = Math.max(0, edge.lengthM - 2.6);
  const doorAlongOffsetM = (phase - 0.5) * doorTravelM;

  // Timber close-detail frontages provide their own complete window rhythm.
  // Mask the window-bearing atlas before adding siding, frames, and panes so
  // one building never renders both the atlas and authored window systems.
  addGeometry(
    wallBuckets,
    facadeMaskKey,
    materials.blankFacade(spec),
    createOrientedBoxGeometry(
      edge,
      0,
      edge.lengthM,
      spec.baseElevationM + facadeHeightM / 2,
      facadeHeightM,
      0.015,
      0.03,
    ),
  );

  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      0,
      edge.lengthM + 0.18,
      spec.baseElevationM + 0.22,
      0.44,
      0.12,
      0.2,
    ),
  );
  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      0,
      edge.lengthM + 0.32,
      eaveElevationM - 0.11,
      0.22,
      0.16,
      0.28,
    ),
  );
  for (const side of [-1, 1] as const) {
    addGeometry(
      wallBuckets,
      trimKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        side * (edge.lengthM / 2 - 0.08),
        0.16,
        spec.baseElevationM + facadeHeightM / 2,
        facadeHeightM,
        0.15,
        0.16,
      ),
    );
  }

  const sidingCourseCount = Math.max(
    1,
    Math.floor(
      (facadeHeightM - 0.55) /
        detailConfig.sidingCourseSpacingM,
    ),
  );
  for (let course = 1; course <= sidingCourseCount; course += 1) {
    addGeometry(
      wallBuckets,
      sidingKey,
      materials.blankFacade(spec),
      createOrientedBoxGeometry(
        edge,
        0,
        edge.lengthM,
        spec.baseElevationM + 0.34 +
          course * detailConfig.sidingCourseSpacingM,
        0.04,
        0.11,
        0.045,
      ),
    );
  }

  const windowPlacements = zverynasWindowPlacements(
    footprint,
    edge,
    spec,
    facadeHeightM,
    doorAlongOffsetM,
    detailConfig,
  );
  const litPlacements = windowPlacements.filter(
    (placement) => placement.litAtNight,
  );
  const unlitPlacements = windowPlacements.filter(
    (placement) => !placement.litAtNight,
  );
  if (litPlacements.length > 0) {
    addGeometry(
      wallBuckets,
      litWindowKey,
      materials.window,
      createOldTownWindowPanelGeometry(edge, litPlacements),
    );
  }
  if (unlitPlacements.length > 0) {
    addGeometry(
      wallBuckets,
      unlitWindowKey,
      materials.windowUnlit,
      createOldTownWindowPanelGeometry(edge, unlitPlacements),
    );
  }
  if (windowPlacements.length > 0) {
    addOldTownWindowSurroundGeometry(
      wallBuckets,
      windowTrimKey,
      trimMaterial,
      edge,
      windowPlacements,
      false,
    );
    for (const placement of windowPlacements) {
      addGeometry(
        wallBuckets,
        windowTrimKey,
        trimMaterial,
        createOrientedBoxGeometry(
          edge,
          placement.alongOffsetM,
          placement.widthM + 0.22,
          placement.centerY - placement.heightM / 2 - 0.03,
          0.09,
          OLD_TOWN_WINDOW_SILL_DEPTH_M / 2,
          OLD_TOWN_WINDOW_SILL_DEPTH_M,
        ),
      );
    }
  }

  addZverynasEntranceGeometry(
    wallBuckets,
    roofBuckets,
    trimKey,
    doorKey,
    metalKey,
    roofKey,
    trimMaterial,
    materials,
    edge,
    spec,
    facadeHeightM,
    eaveElevationM,
    doorAlongOffsetM,
  );
  addOldTownChimneyGeometry(
    wallBuckets,
    chimneyKey,
    trimMaterial,
    footprint,
    edge,
    roofProfile,
    phase,
  );

  if (
    roofRiseM >= 1.25 &&
    (spec.seed >>> 4) % 3 !== 0
  ) {
    const dormerCount = clampInteger(
      edge.lengthM / detailConfig.dormerSpacingM,
      1,
      detailConfig.maximumDormerCount,
    );
    for (let index = 0; index < dormerCount; index += 1) {
      const alongOffsetM =
        -edge.lengthM / 2 +
        edge.lengthM * (index + 0.5) / dormerCount;
      const dormerCenterY = eaveElevationM + roofRiseM * 0.46;
      addGeometry(
        wallBuckets,
        trimKey,
        trimMaterial,
        createOrientedBoxGeometry(
          edge,
          alongOffsetM,
          1.62,
          dormerCenterY,
          0.96,
          0.5,
          0.28,
        ),
      );
      addGeometry(
        wallBuckets,
        unlitWindowKey,
        materials.windowUnlit,
        createOrientedBoxGeometry(
          edge,
          alongOffsetM,
          0.62,
          dormerCenterY,
          0.62,
          0.66,
          0.04,
        ),
      );
      addGeometry(
        roofBuckets,
        roofKey,
        materials.roof(spec),
        createPedimentGeometry(
          edge,
          alongOffsetM,
          1.92,
          dormerCenterY + 0.48,
          eaveElevationM + roofRiseM * 0.92,
          0.68,
        ),
      );
    }
  }
  return true;
}

function addZverynasEntranceGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  roofBuckets: Map<string, GeometryBucket>,
  trimKey: string,
  doorKey: string,
  metalKey: string,
  roofKey: string,
  trimMaterial: GeometryBucket["material"],
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  spec: TimberBuildingVisualSpec,
  facadeHeightM: number,
  eaveElevationM: number,
  alongOffsetM: number,
): void {
  const doorHeightM = Math.min(2.12, facadeHeightM - 0.24);
  if (doorHeightM < 1.62) {
    return;
  }
  const baseElevationM = spec.baseElevationM;
  const doorMaterial = materials.oldTownDoorLeaves.timber;
  addGeometry(
    wallBuckets,
    doorKey,
    doorMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      1.06,
      baseElevationM + 0.12 + doorHeightM / 2,
      doorHeightM,
      0.18,
      0.08,
    ),
  );
  for (const side of [-1, 1] as const) {
    addGeometry(
      wallBuckets,
      trimKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM + side * 0.62,
        0.14,
        baseElevationM + 0.1 + doorHeightM / 2,
        doorHeightM + 0.2,
        0.2,
        0.12,
      ),
    );
  }
  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      1.38,
      baseElevationM + 0.2 + doorHeightM,
      0.16,
      0.2,
      0.12,
    ),
  );
  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      1.48,
      baseElevationM + 0.06,
      0.12,
      0.46,
      0.78,
    ),
  );
  addOldTownDoorLeafDetailGeometry(
    wallBuckets,
    doorKey,
    metalKey,
    doorMaterial,
    materials,
    edge,
    alongOffsetM,
    0.92,
    baseElevationM + 0.16,
    doorHeightM - 0.18,
    0.24,
    spec.seed % 2 === 0 ? "diamond" : "herringbone",
  );

  if ((spec.seed >>> 7) % 3 !== 0) {
    const canopyElevationM = Math.min(
      eaveElevationM - 0.12,
      baseElevationM + doorHeightM + 0.42,
    );
    addGeometry(
      roofBuckets,
      roofKey,
      materials.roof(spec),
      createOrientedBoxGeometry(
        edge,
        alongOffsetM,
        2.18,
        canopyElevationM,
        0.12,
        0.54,
        1.06,
      ),
    );
    for (const side of [-1, 1] as const) {
      const postHeightM = Math.max(
        0.5,
        canopyElevationM - baseElevationM - 0.12,
      );
      addGeometry(
        wallBuckets,
        trimKey,
        trimMaterial,
        createOrientedBoxGeometry(
          edge,
          alongOffsetM + side * 0.88,
          0.1,
          baseElevationM + postHeightM / 2,
          postHeightM,
          0.92,
          0.1,
        ),
      );
    }
  }
}

function zverynasWindowPlacements(
  footprint: Readonly<PolygonWithHoles>,
  edge: Readonly<ReferenceFacadeEdge>,
  spec: TimberBuildingVisualSpec,
  facadeHeightM: number,
  doorAlongOffsetM: number,
  detailConfig: TimberBackgroundDetailConfig,
): readonly OldTownUpperWindowPlacement[] {
  const bounds = footprintBounds(footprint);
  const longestSpanM = Math.max(
    bounds.maxX - bounds.minX,
    bounds.maxZ - bounds.minZ,
  );
  const sourceBayWidthM =
    longestSpanM / Math.max(1, spec.facade.bayCount);
  const columnCount = clampInteger(
    edge.lengthM / Math.max(1, sourceBayWidthM),
    1,
    detailConfig.maximumWindowColumns,
  );
  const rowCount = Math.max(1, spec.floors.count);
  const floorHeightM = facadeHeightM / rowCount;
  const bayWidthM = edge.lengthM / columnCount;
  const widthM = Math.min(
    1.38,
    Math.max(
      0.66,
      bayWidthM * spec.facade.windowRhythm.windowWidthToBayRatio,
    ),
  );
  const heightM = Math.min(
    1.82,
    Math.max(
      0.92,
      floorHeightM *
        spec.facade.windowRhythm.windowHeightToFloorRatio,
    ),
  );
  const placements: OldTownUpperWindowPlacement[] = [];
  for (let row = 0; row < rowCount; row += 1) {
    for (let column = 0; column < columnCount; column += 1) {
      const alongOffsetM =
        -edge.lengthM / 2 + bayWidthM * (column + 0.5);
      if (
        row === 0 &&
        Math.abs(alongOffsetM - doorAlongOffsetM) <
          detailConfig.minimumDoorClearanceM
      ) {
        continue;
      }
      const placement = fitAttachedFacadeWindowPlacement({
        alongOffsetM,
        centerY:
          spec.baseElevationM + floorHeightM * (row + 0.5),
        widthM,
        heightM,
        litAtNight:
          (spec.seed + row * 11 + column * 5) % 4 === 0,
      }, {
        edgeLengthM: edge.lengthM,
        baseElevationM: spec.baseElevationM,
        topElevationM: spec.baseElevationM + facadeHeightM,
        horizontalAdornmentM: 0.14,
        bottomAdornmentM: 0.14,
        topAdornmentM: 0.14,
        minimumPanelWidthM: 0.38,
        minimumPanelHeightM: 0.52,
      });
      if (placement !== null) {
        placements.push(placement);
      }
    }
  }
  return placements;
}

function addOldTownArchitecturalDetailGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  roofBuckets: Map<string, GeometryBucket>,
  materials: CityMaterialLibrary,
  footprint: Readonly<PolygonWithHoles>,
  spec: Readonly<VilniusBuildingVisualSpec>,
  roofProfile: Readonly<RoofProfile>,
  treatment: OldTownFacadeTreatment,
  referencePoint: Readonly<{ x: number; z: number }>,
): boolean {
  const edge = referenceFacadeEdge(footprint, referencePoint);
  if (
    edge === null ||
    edge.lengthM < OLD_TOWN_DETAIL_MINIMUM_FRONTAGE_M
  ) {
    return false;
  }
  const topElevationM = roofProfile.topElevationM;
  const generatedRoofRiseM =
    roofProfile.topElevationM - roofProfile.eaveElevationM;
  const fallbackRoofRiseM = Math.min(
    2.8,
    Math.max(
      1.6,
      (roofProfile.topElevationM - spec.baseElevationM) * 0.18,
    ),
  );
  const eaveElevationM = topElevationM -
    (generatedRoofRiseM > 0.01
      ? generatedRoofRiseM
      : fallbackRoofRiseM);
  const roofRiseM = topElevationM - eaveElevationM;
  const facadeHeightM = eaveElevationM - spec.baseElevationM;
  const streetDetails = oldTownStreetDetailProfileForBuilding(
    spec.buildingId,
  );
  const trimMaterial = materials.architecturalTrim(spec);
  const facadeMaskKey =
    `old-town-detail-facade-mask:${spec.facade.palette.id}`;
  const trimKey = `old-town-detail-trim:${spec.facade.palette.id}`;
  const windowTrimKey =
    `old-town-detail-window-trim:${spec.facade.palette.id}`;
  const windowKey = "old-town-detail-window";
  const upperWindowLitKey = "old-town-detail-upper-window-lit";
  const upperWindowUnlitKey = "old-town-detail-upper-window-unlit";
  const planterKey = "old-town-detail-planter";
  const planterFoliageDarkKey =
    "old-town-detail-planter-foliage-dark";
  const planterFoliageLightKey =
    "old-town-detail-planter-foliage-light";
  const portalLanternBulbKey =
    "old-town-detail-portal-lantern-bulb";
  const metalKey = "old-town-detail-metal";
  const gutterKey = "old-town-detail-gutter";
  const doorKey =
    `old-town-detail-door:${streetDetails.doorLeafStyle}`;
  const doorMaterial =
    materials.oldTownDoorLeaves[streetDetails.doorLeafStyle];
  const chimneyKey =
    `old-town-detail-chimney:${spec.facade.palette.id}`;
  const roofKey = `old-town-detail-roof:${spec.roof.paletteId}`;
  const roofFasciaKey =
    `old-town-detail-roof-fascia:${spec.roof.paletteId}`;

  // The shared facade atlas already contains a low-cost window rhythm. Once
  // this frontage receives authored window geometry, cover that atlas layer
  // so the texture windows cannot appear between or behind the real frames.
  // Informal treatments retain the atlas on the otherwise unauthored ground
  // floor; classical frontages author their ground-floor openings as well.
  const rowCount = Math.max(1, spec.floors.count);
  const authorsGroundFloor =
    treatment === "classical_arcade" || treatment === "gedimino_avenue";
  const authoredFacadeBaseM = authorsGroundFloor
    ? spec.baseElevationM
    : edge.lengthM >= OLD_TOWN_WINDOW_SURROUND_MINIMUM_FRONTAGE_M &&
        rowCount >= 2
      ? spec.baseElevationM + facadeHeightM / rowCount
      : null;
  if (
    authoredFacadeBaseM !== null &&
    eaveElevationM - authoredFacadeBaseM > 0.01
  ) {
    addGeometry(
      wallBuckets,
      facadeMaskKey,
      materials.blankFacade(spec),
      createOrientedBoxGeometry(
        edge,
        0,
        edge.lengthM,
        authoredFacadeBaseM +
          (eaveElevationM - authoredFacadeBaseM) / 2,
        eaveElevationM - authoredFacadeBaseM,
        0.015,
        0.03,
      ),
    );
  }

  // A narrow tile-coloured fascia makes the roof edge legible without the
  // former facade-height apron, which read as a vertical wall of roof tiles.
  addGeometry(
    roofBuckets,
    roofFasciaKey,
    materials.roof(spec),
    createOrientedBoxGeometry(
      edge,
      0,
      edge.lengthM + 0.18,
      eaveElevationM - 0.1,
      0.2,
      0.1,
      0.2,
    ),
  );

  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      0,
      edge.lengthM,
      spec.baseElevationM + 0.28,
      0.56,
      0.08,
      0.16,
    ),
  );
  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      0,
      edge.lengthM + 0.24,
      eaveElevationM - 0.17,
      0.34,
      0.13,
      0.26,
    ),
  );

  for (let row = 1; row < rowCount; row += 1) {
    addGeometry(
      wallBuckets,
      trimKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        0,
        edge.lengthM,
        spec.baseElevationM + facadeHeightM * row / rowCount,
        0.11,
        0.07,
        0.14,
      ),
    );
  }

  addOldTownChimneyGeometry(
    wallBuckets,
    chimneyKey,
    trimMaterial,
    footprint,
    edge,
    roofProfile,
    streetDetails.frontagePhase,
  );
  addOldTownDownpipeGeometry(
    wallBuckets,
    metalKey,
    materials,
    edge,
    spec.baseElevationM,
    eaveElevationM,
    streetDetails.downpipeSide,
  );
  addOldTownGutterGeometry(
    wallBuckets,
    gutterKey,
    materials,
    edge,
    eaveElevationM,
  );
  addOldTownCourtyardDetailGeometry(
    wallBuckets,
    materials,
    footprint,
    spec,
    eaveElevationM,
    treatment,
  );
  if (
    edge.lengthM >= OLD_TOWN_WINDOW_SURROUND_MINIMUM_FRONTAGE_M &&
    spec.floors.count >= 2
  ) {
    addOldTownUpperWindowGeometry(
      wallBuckets,
      windowTrimKey,
      upperWindowLitKey,
      upperWindowUnlitKey,
      planterKey,
      planterFoliageDarkKey,
      planterFoliageLightKey,
      trimMaterial,
      materials,
      footprint,
      edge,
      spec,
      spec.baseElevationM,
      facadeHeightM,
      treatment,
      streetDetails.hasWindowPlanters,
    );
  }
  if (
    (
      streetDetails.hasBalcony ||
      (treatment === "gedimino_avenue" && spec.seed % 3 !== 0)
    ) &&
    spec.floors.count >= 2 &&
    edge.lengthM >= OLD_TOWN_BALCONY_MINIMUM_FRONTAGE_M
  ) {
    addOldTownBalconyGeometry(
      wallBuckets,
      trimKey,
      metalKey,
      trimMaterial,
      materials,
      edge,
      spec.baseElevationM,
      facadeHeightM,
      Math.max(1, spec.floors.count),
      streetDetails.frontagePhase,
    );
  }

  if (
    treatment === "classical_arcade" ||
    treatment === "gedimino_avenue"
  ) {
    const pilasterCount = clampInteger(
      Math.round(edge.lengthM / (treatment === "gedimino_avenue" ? 8 : 12)),
      2,
      treatment === "gedimino_avenue" ? 7 : 5,
    );
    const clearHeightM = Math.max(0.5, facadeHeightM - 0.9);
    for (let index = 0; index <= pilasterCount; index += 1) {
      const alongOffsetM =
        -edge.lengthM / 2 + edge.lengthM * index / pilasterCount;
      addGeometry(
        wallBuckets,
        trimKey,
        trimMaterial,
        createOrientedBoxGeometry(
          edge,
          alongOffsetM,
          0.18,
          spec.baseElevationM + 0.56 + clearHeightM / 2,
          clearHeightM,
          0.1,
          0.2,
        ),
      );
    }
    const openingCount = clampInteger(
      Math.round(edge.lengthM / (treatment === "gedimino_avenue" ? 4.8 : 6.2)),
      2,
      treatment === "gedimino_avenue" ? 9 : 7,
    );
    const doorOpeningIndex = Math.min(
      openingCount - 1,
      Math.floor(streetDetails.frontagePhase * openingCount),
    );
    for (let index = 0; index < openingCount; index += 1) {
      const alongOffsetM =
        -edge.lengthM / 2 +
        edge.lengthM * (index + 0.5) / openingCount;
      const isDoor = index === doorOpeningIndex;
      const opening = fitAttachedFacadeWindowPlacement({
        alongOffsetM,
        centerY: spec.baseElevationM + 1.48,
        widthM: 1.38,
        heightM: 2.4,
        litAtNight: false,
      }, {
        edgeLengthM: edge.lengthM,
        baseElevationM: spec.baseElevationM,
        topElevationM: eaveElevationM,
        horizontalAdornmentM: 0.2,
        bottomAdornmentM: 0.12,
        topAdornmentM: 0.24,
        minimumPanelWidthM: 0.82,
        minimumPanelHeightM: 1.4,
      });
      if (opening === null) {
        continue;
      }
      const openingBottomM =
        opening.centerY - opening.heightM / 2;
      addGeometry(
        wallBuckets,
        isDoor ? trimKey : windowTrimKey,
        trimMaterial,
        createArchGeometry(
          edge,
          opening.alongOffsetM,
          opening.widthM + 0.4,
          opening.heightM + 0.36,
          openingBottomM - 0.12,
          0.115,
        ),
      );
      addGeometry(
        wallBuckets,
        isDoor ? doorKey : windowKey,
        isDoor ? doorMaterial : materials.window,
        createArchGeometry(
          edge,
          opening.alongOffsetM,
          opening.widthM,
          opening.heightM,
          openingBottomM,
          0.13,
        ),
      );
      if (isDoor) {
        addOldTownDoorLeafDetailGeometry(
          wallBuckets,
          doorKey,
          metalKey,
          doorMaterial,
          materials,
          edge,
          opening.alongOffsetM,
          Math.min(1.12, opening.widthM - 0.2),
          openingBottomM + 0.02,
          Math.min(1.78, opening.heightM - 0.2),
          0.19,
          streetDetails.doorPanelPattern,
        );
        if (streetDetails.hasPortalLantern) {
          addOldTownPortalLanternGeometry(
            wallBuckets,
            metalKey,
            portalLanternBulbKey,
            materials,
            edge,
            opening.alongOffsetM,
            opening.widthM,
            spec.baseElevationM,
          );
        }
      }
    }
    if (treatment === "gedimino_avenue") {
      // Gedimino Avenue reads through projecting cornices and occasional
      // centred attic accents even at miniature scale. Keep these attached to
      // the real source facade edge instead of introducing replacement boxes.
      addGeometry(
        wallBuckets,
        trimKey,
        trimMaterial,
        createOrientedBoxGeometry(
          edge,
          0,
          edge.lengthM + 0.42,
          eaveElevationM - 0.03,
          0.32,
          0.2,
          0.4,
        ),
      );
      if (edge.lengthM >= 13 && roofRiseM >= 0.8) {
        addGeometry(
          wallBuckets,
          trimKey,
          trimMaterial,
          createPedimentGeometry(
            edge,
            0,
            Math.min(6.2, edge.lengthM * 0.28),
            eaveElevationM - 0.06,
            Math.min(topElevationM, eaveElevationM + 1.35),
            0.34,
          ),
        );
      }
      if (edge.lengthM >= 19 && roofRiseM >= 1.25) {
        // Long avenue elevations rarely terminate as one anonymous roof slab.
        // Two restrained, wall-hosted attic bays break the source roofline
        // while preserving the official footprint and measured roof height.
        for (const side of [-1, 1] as const) {
          const attic = fitAttachedFacadePlacement({
            alongOffsetM: side * edge.lengthM * 0.27,
            centerY: eaveElevationM + roofRiseM * 0.46,
            widthM: Math.min(2.2, edge.lengthM * 0.1),
            heightM: Math.min(1.08, roofRiseM * 0.52),
          }, {
            edgeLengthM: edge.lengthM,
            baseElevationM: eaveElevationM,
            topElevationM,
            horizontalAdornmentM: 0.16,
            bottomAdornmentM: 0.12,
            topAdornmentM: 0.28,
            minimumPanelWidthM: 1.1,
            minimumPanelHeightM: 0.48,
          });
          if (attic === null) {
            continue;
          }
          addGeometry(
            wallBuckets,
            trimKey,
            trimMaterial,
            createOrientedBoxGeometry(
              edge,
              attic.alongOffsetM,
              attic.widthM + 0.34,
              attic.centerY,
              attic.heightM + 0.26,
              0.28,
              0.42,
            ),
          );
          addGeometry(
            wallBuckets,
            windowKey,
            materials.windowUnlit,
            createOrientedBoxGeometry(
              edge,
              attic.alongOffsetM,
              attic.widthM,
              attic.centerY,
              attic.heightM,
              0.31,
              0.05,
            ),
          );
          addGeometry(
            roofBuckets,
            roofKey,
            materials.roof(spec),
            createPedimentGeometry(
              edge,
              attic.alongOffsetM,
              attic.widthM + 0.58,
              attic.centerY + attic.heightM / 2,
              Math.min(topElevationM, attic.centerY + attic.heightM),
              0.52,
            ),
          );
        }
      }
    }
    if (treatment === "classical_arcade") {
      addOldTownRoofWindowGeometry(
        roofBuckets,
        materials,
        edge,
        spec,
        roofProfile,
        eaveElevationM,
        topElevationM,
      );
    }
    return true;
  }

  if (edge.lengthM >= OLD_TOWN_DOOR_MINIMUM_FRONTAGE_M) {
    addOldTownFramedEntranceGeometry(
      wallBuckets,
      trimKey,
      windowKey,
      doorKey,
      metalKey,
      portalLanternBulbKey,
      trimMaterial,
      doorMaterial,
      materials,
      edge,
      spec.baseElevationM,
      eaveElevationM,
      streetDetails.frontagePhase,
      streetDetails.hasShopCanopy,
      streetDetails.hasPortalLantern,
      streetDetails.doorPanelPattern,
    );
  }

  addOldTownRoofWindowGeometry(
    roofBuckets,
    materials,
    edge,
    spec,
    roofProfile,
    eaveElevationM,
    topElevationM,
  );
  return true;
}

function addOldTownRoofWindowGeometry(
  roofBuckets: Map<string, GeometryBucket>,
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  spec: Readonly<VilniusBuildingVisualSpec>,
  roofProfile: Readonly<RoofProfile>,
  eaveElevationM: number,
  topElevationM: number,
): void {
  const roofRiseM = topElevationM - eaveElevationM;
  if (roofRiseM < 0.65 || spec.roof.type === "flat") {
    return;
  }

  const trimKey = `old-town-detail-roof-window-trim:${spec.facade.palette.id}`;
  const paneKey = "old-town-detail-roof-window-pane";
  const roofMaterial = materials.roof(spec);
  let surface: RoofWindowSurface | null = null;

  if (
    roofProfile.frame !== null &&
    (spec.roof.type === "gable" || spec.roof.type === "hip")
  ) {
    surface = roofWindowSurfaceForProfile(
      roofProfile,
      spec.roof.type,
      edge,
    );
  } else {
    // Concave and courtyard footprints cannot share one safe rectangular roof
    // frame. Give the selected street elevation a bounded, facade-hosted roof
    // apron instead of placing upright dormer boxes in empty space.
    const runM = Math.min(
      OLD_TOWN_COMPLEX_ROOF_APRON_MAXIMUM_RUN_M,
      Math.max(
        OLD_TOWN_COMPLEX_ROOF_APRON_MINIMUM_RUN_M,
        roofRiseM * 1.45,
      ),
    );
    const apronWidthM = Math.max(
      0,
      edge.lengthM - OLD_TOWN_ROOF_WINDOW_EDGE_CLEARANCE_M * 0.8,
    );
    if (apronWidthM < OLD_TOWN_ROOF_WINDOW_WIDTH_M + 0.6) {
      return;
    }
    addGeometry(
      roofBuckets,
      `old-town-detail-roof-apron:${spec.roof.paletteId}`,
      roofMaterial,
      createFacadeRoofApronGeometry(
        edge,
        apronWidthM,
        eaveElevationM,
        topElevationM,
        runM,
      ),
    );
    surface = roofWindowSurfaceForFacadeApron(
      edge,
      apronWidthM,
      eaveElevationM,
      topElevationM,
      runM,
    );
  }

  if (surface === null) {
    return;
  }
  const usableAlongM =
    surface.maxAlongM - surface.minAlongM -
    OLD_TOWN_ROOF_WINDOW_EDGE_CLEARANCE_M * 2;
  if (usableAlongM < OLD_TOWN_ROOF_WINDOW_WIDTH_M) {
    return;
  }
  const count = clampInteger(
    Math.round(usableAlongM / OLD_TOWN_ROOF_WINDOW_SPACING_M),
    1,
    OLD_TOWN_ROOF_WINDOW_MAXIMUM_COUNT,
  );
  const frameWidthM =
    OLD_TOWN_ROOF_WINDOW_WIDTH_M +
    OLD_TOWN_ROOF_WINDOW_FRAME_MARGIN_M * 2;
  const frameHeightM = Math.min(
    OLD_TOWN_ROOF_WINDOW_SLOPE_HEIGHT_M +
      OLD_TOWN_ROOF_WINDOW_FRAME_MARGIN_M * 2,
    surface.slopeLengthM * 0.62,
  );
  const paneHeightM = Math.max(
    0.48,
    frameHeightM - OLD_TOWN_ROOF_WINDOW_FRAME_MARGIN_M * 2,
  );
  const placementSpanM = Math.max(0, usableAlongM - frameWidthM);

  for (let index = 0; index < count; index += 1) {
    const ratio = (index + 0.5) / count;
    const alongM =
      (surface.minAlongM + surface.maxAlongM) / 2 +
      (ratio - 0.5) * placementSpanM;
    addGeometry(
      roofBuckets,
      trimKey,
      materials.architecturalTrim(spec),
      createRoofSurfaceBoxGeometry(
        surface,
        alongM,
        OLD_TOWN_ROOF_WINDOW_SLOPE_PROGRESS,
        frameWidthM,
        frameHeightM,
        0.08,
        OLD_TOWN_ROOF_WINDOW_SURFACE_OFFSET_M,
      ),
    );
    addGeometry(
      roofBuckets,
      paneKey,
      materials.windowUnlit,
      createRoofSurfaceBoxGeometry(
        surface,
        alongM,
        OLD_TOWN_ROOF_WINDOW_SLOPE_PROGRESS,
        OLD_TOWN_ROOF_WINDOW_WIDTH_M,
        paneHeightM,
        0.045,
        OLD_TOWN_ROOF_WINDOW_SURFACE_OFFSET_M + 0.065,
      ),
    );
  }
}

function addOldTownCourtyardDetailGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  materials: CityMaterialLibrary,
  footprint: Readonly<PolygonWithHoles>,
  spec: Readonly<VilniusBuildingVisualSpec>,
  eaveElevationM: number,
  treatment: OldTownFacadeTreatment,
): void {
  const profile = oldTownCourtyardDetailProfileForBuilding(
    spec.buildingId,
  );
  const trimMaterial = materials.architecturalTrim(spec);
  const facadeKey = `old-town-courtyard-facade:${spec.facade.palette.id}`;
  const trimKey = `old-town-courtyard-trim:${spec.facade.palette.id}`;
  const windowTrimKey =
    `old-town-courtyard-window-trim:${spec.facade.palette.id}`;
  const metalKey = "old-town-courtyard-metal";
  const gutterKey = "old-town-courtyard-gutter";
  const windowLitKey = "old-town-courtyard-window-lit";
  const windowUnlitKey = "old-town-courtyard-window-unlit";
  const arcadeWindowKey = "old-town-courtyard-arcade-window";
  const planterKey = "old-town-courtyard-planter";
  const planterFoliageDarkKey =
    "old-town-courtyard-planter-foliage-dark";
  const planterFoliageLightKey =
    "old-town-courtyard-planter-foliage-light";
  const groundPlanterKey = "old-town-courtyard-ground-planter";
  const groundPlanterFoliageDarkKey =
    "old-town-courtyard-ground-planter-foliage-dark";
  const groundPlanterFoliageLightKey =
    "old-town-courtyard-ground-planter-foliage-light";
  const benchTimberKey = "old-town-courtyard-bench-timber";
  const benchMetalKey = "old-town-courtyard-bench-metal";
  const doorKey = "old-town-courtyard-door";
  const transomKey = "old-town-courtyard-door-transom";
  const courtyardDoorDetails = oldTownStreetDetailProfileForBuilding(
    `${spec.buildingId}:courtyard`,
  );
  const courtyardDoorMaterial =
    materials.oldTownDoorLeaves[courtyardDoorDetails.doorLeafStyle];
  for (const hole of footprint.holes) {
    if (!qualifiesForOldTownCourtyardDetail(hole)) {
      continue;
    }
    if (profile.paved) {
      addGeometry(
        wallBuckets,
        "old-town-courtyard-paving",
        materials.streetSurfaces.old_town.pedestrian,
        createCourtyardPavingGeometry(
          hole,
          spec.baseElevationM + 0.018,
        ),
      );
    }
    const ring = normalizedWallRing(hole, true);
    let doorEdgeIndex = -1;
    let doorEdgeLengthM = 0;
    for (let index = 1; index < ring.length; index += 1) {
      const from = ring[index - 1];
      const to = ring[index];
      if (from === undefined || to === undefined) {
        continue;
      }
      const lengthM = Math.hypot(to.x - from.x, to.z - from.z);
      if (
        lengthM >= OLD_TOWN_DOOR_MINIMUM_FRONTAGE_M &&
        lengthM > doorEdgeLengthM
      ) {
        doorEdgeIndex = index;
        doorEdgeLengthM = lengthM;
      }
    }
    for (let index = 1; index < ring.length; index += 1) {
      const from = ring[index - 1];
      const to = ring[index];
      if (from === undefined || to === undefined) {
        continue;
      }
      const deltaX = to.x - from.x;
      const deltaZ = to.z - from.z;
      const lengthM = Math.hypot(deltaX, deltaZ);
      if (lengthM < OLD_TOWN_COURTYARD_DETAIL_MINIMUM_EDGE_M) {
        continue;
      }
      const along = { x: deltaX / lengthM, z: deltaZ / lengthM };
      // Hole rings are clockwise, so their right-hand normal faces the open
      // courtyard rather than the surrounding building mass.
      const edge: ReferenceFacadeEdge = {
        midpoint: {
          x: (from.x + to.x) / 2,
          z: (from.z + to.z) / 2,
        },
        along,
        outward: { x: along.z, z: -along.x },
        lengthM,
      };
      addGeometry(
        wallBuckets,
        facadeKey,
        materials.blankFacade(spec),
        createOrientedBoxGeometry(
          edge,
          0,
          lengthM,
          spec.baseElevationM +
            (eaveElevationM - spec.baseElevationM) / 2,
          eaveElevationM - spec.baseElevationM,
          // Courtyard source walls can overlap at shared footprint edges.
          // Keep the front face at 0.16 m: ahead of either atlas-bearing
          // source wall, but still behind the authored 0.18 m window panels.
          0.08,
          0.16,
        ),
      );
      addGeometry(
        wallBuckets,
        trimKey,
        trimMaterial,
        createOrientedBoxGeometry(
          edge,
          0,
          lengthM,
          spec.baseElevationM +
            OLD_TOWN_COURTYARD_PLINTH_HEIGHT_M / 2,
          OLD_TOWN_COURTYARD_PLINTH_HEIGHT_M,
          0.045,
          0.09,
        ),
      );
      addOldTownGutterGeometry(
        wallBuckets,
        gutterKey,
        materials,
        edge,
        eaveElevationM,
      );
      addGeometry(
        wallBuckets,
        trimKey,
        trimMaterial,
        createOrientedBoxGeometry(
          edge,
          0,
          lengthM + 0.12,
          eaveElevationM - OLD_TOWN_COURTYARD_EAVE_HEIGHT_M / 2,
          OLD_TOWN_COURTYARD_EAVE_HEIGHT_M,
          0.07,
          0.14,
        ),
      );
      if (
        lengthM >= OLD_TOWN_WINDOW_SURROUND_MINIMUM_FRONTAGE_M &&
        spec.floors.count >= 2
      ) {
        addOldTownUpperWindowGeometry(
          wallBuckets,
          windowTrimKey,
          windowLitKey,
          windowUnlitKey,
          planterKey,
          planterFoliageDarkKey,
          planterFoliageLightKey,
          trimMaterial,
          materials,
          footprint,
          edge,
          spec,
          spec.baseElevationM,
          eaveElevationM - spec.baseElevationM,
          profile.classicalWindowSurrounds
            ? "classical_arcade"
            : treatment,
          profile.hasWindowPlanters,
          true,
          index === doorEdgeIndex ? 0 : null,
        );
      }
      if (
        profile.classicalWindowSurrounds &&
        lengthM >= OLD_TOWN_WINDOW_SURROUND_MINIMUM_FRONTAGE_M
      ) {
        const openingCount = clampInteger(
          Math.round(lengthM / 4.2),
          2,
          6,
        );
        const doorOpeningIndex = index === doorEdgeIndex
          ? Math.floor(openingCount / 2)
          : -1;
        for (
          let openingIndex = 0;
          openingIndex < openingCount;
          openingIndex += 1
        ) {
          if (openingIndex === doorOpeningIndex) {
            continue;
          }
          const alongOffsetM =
            -lengthM / 2 +
            lengthM * (openingIndex + 0.5) / openingCount;
          const opening = fitAttachedFacadeWindowPlacement({
            alongOffsetM,
            centerY: spec.baseElevationM + 1.51,
            widthM: 1.42,
            heightM: 2.3,
            litAtNight: false,
          }, {
            edgeLengthM: edge.lengthM,
            baseElevationM: spec.baseElevationM,
            topElevationM: eaveElevationM,
            horizontalAdornmentM: 0.2,
            bottomAdornmentM: 0.18,
            topAdornmentM: 0.2,
            minimumPanelWidthM: 0.82,
            minimumPanelHeightM: 1.3,
          });
          if (opening === null) {
            continue;
          }
          const openingBottomM =
            opening.centerY - opening.heightM / 2;
          addGeometry(
            wallBuckets,
            windowTrimKey,
            trimMaterial,
            createArchGeometry(
              edge,
              opening.alongOffsetM,
              opening.widthM + 0.4,
              opening.heightM + 0.38,
              openingBottomM - 0.18,
              0.13,
            ),
          );
          addGeometry(
            wallBuckets,
            arcadeWindowKey,
            materials.window,
            createArchGeometry(
              edge,
              opening.alongOffsetM,
              opening.widthM,
              opening.heightM,
              openingBottomM,
              0.145,
            ),
          );
        }
      }
      if (index === doorEdgeIndex) {
        addOldTownFramedEntranceGeometry(
          wallBuckets,
          trimKey,
          transomKey,
          doorKey,
          metalKey,
          "old-town-courtyard-unused-lantern",
          trimMaterial,
          courtyardDoorMaterial,
          materials,
          edge,
          spec.baseElevationM,
          eaveElevationM,
          0.5,
          false,
          false,
          courtyardDoorDetails.doorPanelPattern,
        );
        if (
          lengthM >= OLD_TOWN_COURTYARD_FURNITURE_MINIMUM_EDGE_M &&
          profile.hasGroundPlanter
        ) {
          addOldTownCourtyardGroundPlanterGeometry(
            wallBuckets,
            groundPlanterKey,
            groundPlanterFoliageDarkKey,
            groundPlanterFoliageLightKey,
            materials,
            edge,
            spec.baseElevationM,
            Math.min(4.2, lengthM * 0.27),
            spec.seed,
          );
        }
        if (
          lengthM >= OLD_TOWN_COURTYARD_FURNITURE_MINIMUM_EDGE_M &&
          profile.hasBench
        ) {
          addOldTownCourtyardBenchGeometry(
            wallBuckets,
            benchTimberKey,
            benchMetalKey,
            courtyardDoorMaterial,
            materials,
            edge,
            spec.baseElevationM,
            -Math.min(4.2, lengthM * 0.27),
          );
        }
      }
      if (index % 2 === 1) {
        const pipeHeightM = Math.max(
          0.5,
          eaveElevationM - spec.baseElevationM - 0.2,
        );
        addGeometry(
          wallBuckets,
          metalKey,
          materials.streetPole,
          createOrientedBoxGeometry(
            edge,
            Math.max(0, lengthM / 2 - 0.2),
            0.1,
            spec.baseElevationM + 0.1 + pipeHeightM / 2,
            pipeHeightM,
            0.11,
            0.1,
          ),
        );
      }
    }
  }
}

function qualifiesForOldTownCourtyardDetail(
  hole: Readonly<LinearRing>,
): boolean {
  if (
    Math.abs(signedRingAreaM2(hole)) <
      OLD_TOWN_COURTYARD_DETAIL_MINIMUM_AREA_M2
  ) {
    return false;
  }
  const ring = normalizedWallRing(hole, true);
  for (let index = 1; index < ring.length; index += 1) {
    const from = ring[index - 1];
    const to = ring[index];
    if (
      from !== undefined &&
      to !== undefined &&
      Math.hypot(to.x - from.x, to.z - from.z) >=
        OLD_TOWN_COURTYARD_DETAIL_MINIMUM_EDGE_M
    ) {
      return true;
    }
  }
  return false;
}

function addOldTownCourtyardGroundPlanterGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  planterKey: string,
  foliageDarkKey: string,
  foliageLightKey: string,
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  baseElevationM: number,
  alongOffsetM: number,
  seed: number,
): void {
  addGeometry(
    wallBuckets,
    planterKey,
    materials.oldTownPlanter,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      1.8,
      baseElevationM + 0.24,
      0.44,
      0.42,
      0.62,
    ),
  );
  for (let clumpIndex = -1; clumpIndex <= 1; clumpIndex += 1) {
    const light = (seed + clumpIndex + 1) % 2 === 0;
    addGeometry(
      wallBuckets,
      light ? foliageLightKey : foliageDarkKey,
      light ? materials.treeCrownLight : materials.treeCrownDark,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM + clumpIndex * 0.5,
        clumpIndex === 0 ? 0.58 : 0.46,
        baseElevationM + (clumpIndex === 0 ? 0.62 : 0.56),
        clumpIndex === 0 ? 0.54 : 0.4,
        0.45,
        clumpIndex === 0 ? 0.56 : 0.48,
      ),
    );
  }
}

function addOldTownCourtyardBenchGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  timberKey: string,
  metalKey: string,
  timberMaterial: GeometryBucket["material"],
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  baseElevationM: number,
  alongOffsetM: number,
): void {
  addGeometry(
    wallBuckets,
    timberKey,
    timberMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      1.8,
      baseElevationM + 0.48,
      0.12,
      0.78,
      0.52,
    ),
  );
  addGeometry(
    wallBuckets,
    timberKey,
    timberMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      1.8,
      baseElevationM + 0.83,
      0.58,
      0.98,
      0.1,
    ),
  );
  for (const legOffsetM of [-0.62, 0.62]) {
    addGeometry(
      wallBuckets,
      metalKey,
      materials.streetPole,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM + legOffsetM,
        0.08,
        baseElevationM + 0.25,
        0.5,
        0.78,
        0.08,
      ),
    );
  }
}

function createCourtyardPavingGeometry(
  ring: Readonly<LinearRing>,
  elevationM: number,
): BufferGeometry {
  const geometry = new ShapeGeometry(
    createPolygonShape({ outer: ring, holes: [] }),
    1,
  );
  geometry.rotateX(-Math.PI / 2);
  const positions = geometry.getAttribute("position");
  for (let index = 0; index < positions.count; index += 1) {
    positions.setY(index, elevationM);
  }
  positions.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function addOldTownChimneyGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  chimneyKey: string,
  material: GeometryBucket["material"],
  footprint: Readonly<PolygonWithHoles>,
  edge: Readonly<ReferenceFacadeEdge>,
  roofProfile: Readonly<RoofProfile>,
  frontagePhase: number,
): void {
  const chimneyCount = clampInteger(
    Math.round(edge.lengthM / OLD_TOWN_CHIMNEY_SPACING_M),
    1,
    OLD_TOWN_CHIMNEY_MAXIMUM_COUNT,
  );
  const frontProjection =
    edge.midpoint.x * edge.outward.x +
    edge.midpoint.z * edge.outward.z;
  let rearProjection = frontProjection;
  for (const point of footprint.outer) {
    rearProjection = Math.min(
      rearProjection,
      point.x * edge.outward.x + point.z * edge.outward.z,
    );
  }
  const footprintDepthM = Math.max(1.2, frontProjection - rearProjection);
  const inwardOffsetM = -Math.min(4, footprintDepthM * 0.48);
  const spacingM = edge.lengthM / chimneyCount;
  const jitterM = (frontagePhase - 0.5) *
    Math.min(1.2, spacingM * 0.25);
  for (let index = 0; index < chimneyCount; index += 1) {
    const alongOffsetM =
      -edge.lengthM / 2 +
      spacingM * (index + 0.5) +
      (index % 2 === 0 ? jitterM : -jitterM);
    addGeometry(
      wallBuckets,
      chimneyKey,
      material,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM,
        0.62,
        roofProfile.topElevationM + 0.42,
        1.45,
        inwardOffsetM,
        0.62,
      ),
    );
    addGeometry(
      wallBuckets,
      chimneyKey,
      material,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM,
        0.78,
        roofProfile.topElevationM + 1.15,
        0.14,
        inwardOffsetM,
        0.78,
      ),
    );
  }
}

function addOldTownDownpipeGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  metalKey: string,
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  baseElevationM: number,
  eaveElevationM: number,
  side: "left" | "right",
): void {
  const pipeHeightM = Math.max(0.5, eaveElevationM - baseElevationM - 0.2);
  const sideOffsetM = side === "left" ? -1 : 1;
  const addPipeAt = (alongOffsetM: number): void => {
    addGeometry(
      wallBuckets,
      metalKey,
      materials.streetPole,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM,
        0.11,
        baseElevationM + 0.1 + pipeHeightM / 2,
        pipeHeightM,
        0.15,
        0.11,
      ),
    );
  };
  addPipeAt(sideOffsetM * Math.max(0, edge.lengthM / 2 - 0.22));
  if (edge.lengthM >= 24) {
    addPipeAt(-sideOffsetM * Math.max(0, edge.lengthM / 2 - 0.22));
  }
}

function addOldTownGutterGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  gutterKey: string,
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  eaveElevationM: number,
): void {
  // The shallow continuous channel joins the existing downpipes at the eave.
  // Keeping it rectangular preserves the project's low-detail miniature
  // language while making the drainage system read as one coherent detail.
  addGeometry(
    wallBuckets,
    gutterKey,
    materials.streetPole,
    createOrientedBoxGeometry(
      edge,
      0,
      edge.lengthM + 0.14,
      eaveElevationM - OLD_TOWN_GUTTER_HEIGHT_M / 2,
      OLD_TOWN_GUTTER_HEIGHT_M,
      OLD_TOWN_GUTTER_DEPTH_M / 2,
      OLD_TOWN_GUTTER_DEPTH_M,
    ),
  );
}

export interface FacadeAttachmentPlacement {
  readonly alongOffsetM: number;
  readonly centerY: number;
  readonly widthM: number;
  readonly heightM: number;
}

export interface FacadeWindowPlacement extends FacadeAttachmentPlacement {
  readonly litAtNight: boolean;
}

export interface FacadeAttachmentBounds {
  readonly edgeLengthM: number;
  readonly baseElevationM: number;
  readonly topElevationM: number;
  /** Additional trim, sill, or crown width beyond each side of the pane. */
  readonly horizontalAdornmentM: number;
  /** Additional trim below the pane. */
  readonly bottomAdornmentM: number;
  /** Additional trim or crown above the pane. */
  readonly topAdornmentM: number;
  readonly minimumPanelWidthM: number;
  readonly minimumPanelHeightM: number;
}

/**
 * Fits a procedural facade attachment to its source-footprint host wall.
 *
 * Doors, balconies, awnings, signs, cornices and windows all use the same
 * lateral and vertical contract. The returned panel and all declared
 * ornament remain inside the host wall envelope; an attachment that cannot
 * retain its minimum useful size is omitted rather than floating beside or
 * above a building.
 */
export function fitAttachedFacadePlacement<
  Placement extends FacadeAttachmentPlacement,
>(
  placement: Readonly<Placement>,
  bounds: Readonly<FacadeAttachmentBounds>,
): Placement | null {
  const values = [
    placement.alongOffsetM,
    placement.centerY,
    placement.widthM,
    placement.heightM,
    bounds.edgeLengthM,
    bounds.baseElevationM,
    bounds.topElevationM,
    bounds.horizontalAdornmentM,
    bounds.bottomAdornmentM,
    bounds.topAdornmentM,
    bounds.minimumPanelWidthM,
    bounds.minimumPanelHeightM,
  ];
  if (
    values.some((value) => !Number.isFinite(value)) ||
    placement.widthM <= 0 ||
    placement.heightM <= 0 ||
    bounds.edgeLengthM <= 0 ||
    bounds.topElevationM <= bounds.baseElevationM ||
    bounds.horizontalAdornmentM < 0 ||
    bounds.bottomAdornmentM < 0 ||
    bounds.topAdornmentM < 0 ||
    bounds.minimumPanelWidthM <= 0 ||
    bounds.minimumPanelHeightM <= 0
  ) {
    return null;
  }

  const availableHalfWidthM =
    bounds.edgeLengthM / 2 -
    bounds.horizontalAdornmentM -
    Math.abs(placement.alongOffsetM);
  const availableHalfHeightM = Math.min(
    placement.centerY -
      (bounds.baseElevationM + bounds.bottomAdornmentM),
    bounds.topElevationM -
      bounds.topAdornmentM -
      placement.centerY,
  );
  const widthM = Math.min(
    placement.widthM,
    availableHalfWidthM * 2,
  );
  const heightM = Math.min(
    placement.heightM,
    availableHalfHeightM * 2,
  );
  if (
    widthM < bounds.minimumPanelWidthM ||
    heightM < bounds.minimumPanelHeightM
  ) {
    return null;
  }
  return {
    ...placement,
    widthM,
    heightM,
  } as Placement;
}

/** Window-specific compatibility wrapper for existing facade grammars. */
export function fitAttachedFacadeWindowPlacement(
  placement: Readonly<FacadeWindowPlacement>,
  bounds: Readonly<FacadeAttachmentBounds>,
): FacadeWindowPlacement | null {
  return fitAttachedFacadePlacement(placement, bounds);
}

type OldTownUpperWindowPlacement = FacadeWindowPlacement;

function addOldTownUpperWindowGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  trimKey: string,
  litWindowKey: string,
  unlitWindowKey: string,
  planterKey: string,
  planterFoliageDarkKey: string,
  planterFoliageLightKey: string,
  trimMaterial: GeometryBucket["material"],
  materials: CityMaterialLibrary,
  footprint: Readonly<PolygonWithHoles>,
  edge: Readonly<ReferenceFacadeEdge>,
  spec: Readonly<VilniusBuildingVisualSpec>,
  baseElevationM: number,
  facadeHeightM: number,
  treatment: OldTownFacadeTreatment,
  hasWindowPlanters: boolean,
  includeGroundFloor = false,
  groundFloorOpeningAlongOffsetM: number | null = null,
): void {
  const placements = oldTownUpperWindowPlacements(
    footprint,
    edge,
    spec,
    baseElevationM,
    facadeHeightM,
    includeGroundFloor,
    treatment !== "pastel_dormered",
  ).filter((placement) =>
    groundFloorOpeningAlongOffsetM === null ||
    placement.centerY >= baseElevationM + facadeHeightM /
      Math.max(1, spec.floors.count) ||
    Math.abs(placement.alongOffsetM - groundFloorOpeningAlongOffsetM) > 1.25
  );
  if (placements.length === 0) {
    return;
  }
  const litPlacements = placements.filter(
    (placement) => placement.litAtNight,
  );
  const unlitPlacements = placements.filter(
    (placement) => !placement.litAtNight,
  );
  if (litPlacements.length > 0) {
    addGeometry(
      wallBuckets,
      litWindowKey,
      materials.window,
      createOldTownWindowPanelGeometry(edge, litPlacements),
    );
  }
  if (unlitPlacements.length > 0) {
    addGeometry(
      wallBuckets,
      unlitWindowKey,
      materials.windowUnlit,
      createOldTownWindowPanelGeometry(edge, unlitPlacements),
    );
  }
  addOldTownWindowSurroundGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    edge,
    placements,
    treatment !== "pastel_dormered",
  );
  for (const placement of placements) {
    addGeometry(
      wallBuckets,
      trimKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        placement.alongOffsetM,
        placement.widthM + 0.26,
        placement.centerY - placement.heightM / 2 - 0.035,
        0.1,
        OLD_TOWN_WINDOW_SILL_DEPTH_M / 2,
        OLD_TOWN_WINDOW_SILL_DEPTH_M,
      ),
    );
  }
  if (hasWindowPlanters) {
    addOldTownWindowPlanterGeometry(
      wallBuckets,
      planterKey,
      planterFoliageDarkKey,
      planterFoliageLightKey,
      materials,
      edge,
      placements,
      spec.seed,
    );
  }
}

function addOldTownWindowPlanterGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  planterKey: string,
  foliageDarkKey: string,
  foliageLightKey: string,
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  placements: readonly Readonly<OldTownUpperWindowPlacement>[],
  seed: number,
): void {
  const selected = placements.filter(
    (_placement, index) => (index + seed) % 3 === 0,
  );
  for (const [planterIndex, placement] of selected.entries()) {
    const planterCenterY =
      placement.centerY - placement.heightM / 2 + 0.04;
    addGeometry(
      wallBuckets,
      planterKey,
      materials.oldTownPlanter,
      createOrientedBoxGeometry(
        edge,
        placement.alongOffsetM,
        placement.widthM + 0.14,
        planterCenterY,
        0.2,
        0.42,
        0.28,
      ),
    );
    const clumpWidthM = Math.min(
      0.22,
      Math.max(0.14, placement.widthM / 5),
    );
    for (let clumpIndex = -1; clumpIndex <= 1; clumpIndex += 1) {
      const light = (seed + planterIndex + clumpIndex + 3) % 2 === 0;
      addGeometry(
        wallBuckets,
        light ? foliageLightKey : foliageDarkKey,
        light ? materials.treeCrownLight : materials.treeCrownDark,
        createOrientedBoxGeometry(
          edge,
          placement.alongOffsetM +
            clumpIndex * placement.widthM * 0.24,
          clumpWidthM,
          planterCenterY + 0.16 +
            (clumpIndex === 0 ? 0.04 : 0),
          clumpIndex === 0 ? 0.28 : 0.2,
          0.46,
          0.18,
        ),
      );
    }
  }
}

function oldTownUpperWindowPlacements(
  footprint: Readonly<PolygonWithHoles>,
  edge: Readonly<ReferenceFacadeEdge>,
  spec: Readonly<VilniusBuildingVisualSpec>,
  baseElevationM: number,
  facadeHeightM: number,
  includeGroundFloor: boolean,
  classical: boolean,
): readonly OldTownUpperWindowPlacement[] {
  const bounds = footprintBounds(footprint);
  const longestSpanM = Math.max(
    bounds.maxX - bounds.minX,
    bounds.maxZ - bounds.minZ,
  );
  const sourceBayWidthM =
    longestSpanM / Math.max(1, spec.facade.bayCount);
  const columnCount = clampInteger(
    edge.lengthM / Math.max(1, sourceBayWidthM),
    1,
    OLD_TOWN_WINDOW_SURROUND_MAXIMUM_COLUMNS,
  );
  const rowCount = Math.max(1, spec.floors.count);
  const floorHeightM = facadeHeightM / rowCount;
  const bayWidthM = edge.lengthM / columnCount;
  const widthM = Math.min(
    1.5,
    Math.max(
      0.68,
      bayWidthM * spec.facade.windowRhythm.windowWidthToBayRatio,
    ),
  );
  const heightM = Math.min(
    2.15,
    Math.max(
      1.08,
      floorHeightM *
        spec.facade.windowRhythm.windowHeightToFloorRatio,
    ),
  );
  const placements: OldTownUpperWindowPlacement[] = [];
  const firstRow = includeGroundFloor ? 0 : 1;
  for (let row = firstRow; row < rowCount; row += 1) {
    for (let column = 0; column < columnCount; column += 1) {
      const placement = fitAttachedFacadeWindowPlacement({
        alongOffsetM:
          -edge.lengthM / 2 + bayWidthM * (column + 0.5),
        centerY: baseElevationM + floorHeightM * (row + 0.5),
        widthM,
        heightM,
        litAtNight:
          (spec.seed + row * 13 + column * 7) % 4 === 0,
      }, {
        edgeLengthM: edge.lengthM,
        baseElevationM,
        topElevationM: baseElevationM + facadeHeightM,
        // The classical head moulding is wider and taller than the pane.
        horizontalAdornmentM: classical ? 0.16 : 0.14,
        bottomAdornmentM: 0.14,
        topAdornmentM: classical ? 0.3 : 0.14,
        minimumPanelWidthM: 0.44,
        minimumPanelHeightM: 0.56,
      });
      if (placement !== null) {
        placements.push(placement);
      }
    }
  }
  return placements;
}

function createOldTownWindowPanelGeometry(
  edge: Readonly<ReferenceFacadeEdge>,
  placements: readonly Readonly<OldTownUpperWindowPlacement>[],
): BufferGeometry {
  const geometry = createFacadeQuadBatchGeometry(
    edge,
    placements.map((placement) => ({
      alongMinimumM:
        placement.alongOffsetM - placement.widthM / 2,
      alongMaximumM:
        placement.alongOffsetM + placement.widthM / 2,
      bottomElevationM:
        placement.centerY - placement.heightM / 2,
      topElevationM:
        placement.centerY + placement.heightM / 2,
      outwardOffsetM: OLD_TOWN_WINDOW_PANEL_OFFSET_M,
    })),
  );
  geometry.name = "old-town-upper-window-panels";
  return geometry;
}

function addOldTownWindowSurroundGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  trimKey: string,
  trimMaterial: GeometryBucket["material"],
  edge: Readonly<ReferenceFacadeEdge>,
  placements: readonly Readonly<OldTownUpperWindowPlacement>[],
  classical: boolean,
): void {
  const addTrimBox = (
    alongOffsetM: number,
    widthM: number,
    centerY: number,
    heightM: number,
    depthM: number,
  ): void => {
    addGeometry(
      wallBuckets,
      trimKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM,
        widthM,
        centerY,
        heightM,
        depthM / 2,
        depthM,
      ),
    );
  };
  for (const placement of placements) {
    const halfWidthM = placement.widthM / 2;
    const halfHeightM = placement.heightM / 2;
    const frameWidthM = Math.min(
      0.14,
      Math.max(0.08, placement.widthM * 0.09),
    );
    const bottomM = placement.centerY - halfHeightM;
    const topM = placement.centerY + halfHeightM;
    const outerWidthM = placement.widthM + frameWidthM * 2;
    addTrimBox(
      placement.alongOffsetM - halfWidthM - frameWidthM / 2,
      frameWidthM,
      placement.centerY,
      placement.heightM,
      OLD_TOWN_WINDOW_FRAME_DEPTH_M,
    );
    addTrimBox(
      placement.alongOffsetM + halfWidthM + frameWidthM / 2,
      frameWidthM,
      placement.centerY,
      placement.heightM,
      OLD_TOWN_WINDOW_FRAME_DEPTH_M,
    );
    addTrimBox(
      placement.alongOffsetM,
      outerWidthM,
      topM + frameWidthM / 2,
      frameWidthM,
      OLD_TOWN_WINDOW_FRAME_DEPTH_M,
    );
    addTrimBox(
      placement.alongOffsetM,
      outerWidthM,
      bottomM - frameWidthM / 2,
      frameWidthM,
      OLD_TOWN_WINDOW_FRAME_DEPTH_M,
    );
    addTrimBox(
      placement.alongOffsetM,
      0.07,
      placement.centerY,
      placement.heightM,
      OLD_TOWN_WINDOW_MULLION_DEPTH_M + 0.004,
    );
    addTrimBox(
      placement.alongOffsetM,
      placement.widthM,
      placement.centerY,
      0.06,
      OLD_TOWN_WINDOW_MULLION_DEPTH_M,
    );
    if (classical) {
      addTrimBox(
        placement.alongOffsetM,
        placement.widthM + 0.32,
        topM + frameWidthM + 0.1,
        0.12,
        OLD_TOWN_WINDOW_FRAME_DEPTH_M,
      );
    }
  }
}

interface FacadeQuad {
  readonly alongMinimumM: number;
  readonly alongMaximumM: number;
  readonly bottomElevationM: number;
  readonly topElevationM: number;
  readonly outwardOffsetM: number;
}

function createFacadeQuadBatchGeometry(
  edge: Readonly<ReferenceFacadeEdge>,
  quads: readonly Readonly<FacadeQuad>[],
): BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const currentWindingFacesOutward =
    edge.along.z * edge.outward.x -
      edge.along.x * edge.outward.z >= 0;
  const appendPoint = (
    alongOffsetM: number,
    elevationM: number,
    outwardOffsetM: number,
  ): void => {
    positions.push(
      edge.midpoint.x +
        edge.along.x * alongOffsetM +
        edge.outward.x * outwardOffsetM,
      elevationM,
      edge.midpoint.z +
        edge.along.z * alongOffsetM +
        edge.outward.z * outwardOffsetM,
    );
  };
  for (const quad of quads) {
    const vertexOffset = positions.length / 3;
    appendPoint(
      quad.alongMinimumM,
      quad.bottomElevationM,
      quad.outwardOffsetM,
    );
    appendPoint(
      quad.alongMinimumM,
      quad.topElevationM,
      quad.outwardOffsetM,
    );
    appendPoint(
      quad.alongMaximumM,
      quad.topElevationM,
      quad.outwardOffsetM,
    );
    appendPoint(
      quad.alongMaximumM,
      quad.bottomElevationM,
      quad.outwardOffsetM,
    );
    uvs.push(0, 0, 0, 1, 1, 1, 1, 0);
    if (currentWindingFacesOutward) {
      indices.push(
        vertexOffset,
        vertexOffset + 1,
        vertexOffset + 2,
        vertexOffset,
        vertexOffset + 2,
        vertexOffset + 3,
      );
    } else {
      indices.push(
        vertexOffset,
        vertexOffset + 2,
        vertexOffset + 1,
        vertexOffset,
        vertexOffset + 3,
        vertexOffset + 2,
      );
    }
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function addOldTownBalconyGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  trimKey: string,
  metalKey: string,
  trimMaterial: GeometryBucket["material"],
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  baseElevationM: number,
  facadeHeightM: number,
  rowCount: number,
  frontagePhase: number,
): void {
  const requestedWidthM = Math.min(
    3.2,
    Math.max(2.5, edge.lengthM * 0.18),
  );
  const safeTravelM = Math.max(0, edge.lengthM - requestedWidthM - 1.4);
  const requestedAlongOffsetM = (frontagePhase - 0.5) * safeTravelM;
  const slabY = baseElevationM + facadeHeightM / rowCount + 0.16;
  const hosted = fitAttachedFacadePlacement({
    alongOffsetM: requestedAlongOffsetM,
    centerY: slabY + 0.48,
    widthM: requestedWidthM,
    heightM: 1.02,
  }, {
    edgeLengthM: edge.lengthM,
    baseElevationM,
    topElevationM: baseElevationM + facadeHeightM,
    horizontalAdornmentM: 0.12,
    bottomAdornmentM: 0.08,
    topAdornmentM: 0.08,
    minimumPanelWidthM: 1.8,
    minimumPanelHeightM: 0.64,
  });
  if (hosted === null) {
    return;
  }
  const { alongOffsetM, widthM } = hosted;
  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      widthM,
      slabY,
      0.14,
      0.52,
      1.02,
    ),
  );
  addGeometry(
    wallBuckets,
    metalKey,
    materials.streetPole,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      widthM,
      slabY + 0.88,
      0.08,
      1.02,
      0.08,
    ),
  );
  for (let index = 0; index < 5; index += 1) {
    addGeometry(
      wallBuckets,
      metalKey,
      materials.streetPole,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM - widthM / 2 + widthM * index / 4,
        0.06,
        slabY + 0.48,
        0.82,
        1.02,
        0.06,
      ),
    );
  }
}

function addOldTownDoorLeafDetailGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  doorKey: string,
  metalKey: string,
  doorMaterial: GeometryBucket["material"],
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  alongOffsetM: number,
  widthM: number,
  bottomElevationM: number,
  heightM: number,
  outwardOffsetM: number,
  pattern: OldTownDoorPanelPattern,
): void {
  const halfWidthM = widthM / 2;
  const centerElevationM = bottomElevationM + heightM / 2;
  addGeometry(
    wallBuckets,
    doorKey,
    doorMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      0.075,
      centerElevationM,
      heightM * 0.9,
      outwardOffsetM,
      0.04,
    ),
  );
  for (const elevationRatio of [0.12, 0.5, 0.88]) {
    addGeometry(
      wallBuckets,
      doorKey,
      doorMaterial,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM,
        widthM * 0.9,
        bottomElevationM + heightM * elevationRatio,
        0.075,
        outwardOffsetM,
        0.04,
      ),
    );
  }

  const braceSegments: Array<Readonly<{
    fromAlongM: number;
    fromElevationM: number;
    toAlongM: number;
    toElevationM: number;
  }>> = [];
  if (pattern === "diamond") {
    const halfDiamondWidthM = widthM * 0.34;
    const halfDiamondHeightM = heightM * 0.26;
    const points = [
      {
        alongM: alongOffsetM,
        elevationM: centerElevationM + halfDiamondHeightM,
      },
      {
        alongM: alongOffsetM + halfDiamondWidthM,
        elevationM: centerElevationM,
      },
      {
        alongM: alongOffsetM,
        elevationM: centerElevationM - halfDiamondHeightM,
      },
      {
        alongM: alongOffsetM - halfDiamondWidthM,
        elevationM: centerElevationM,
      },
    ] as const;
    for (let index = 0; index < points.length; index += 1) {
      const from = points[index]!;
      const to = points[(index + 1) % points.length]!;
      braceSegments.push({
        fromAlongM: from.alongM,
        fromElevationM: from.elevationM,
        toAlongM: to.alongM,
        toElevationM: to.elevationM,
      });
    }
  } else {
    for (const rowRatio of [0.3, 0.7]) {
      const rowElevationM =
        bottomElevationM + heightM * rowRatio;
      const peakElevationM = rowElevationM + heightM * 0.14;
      braceSegments.push(
        {
          fromAlongM: alongOffsetM - halfWidthM * 0.72,
          fromElevationM: rowElevationM,
          toAlongM: alongOffsetM,
          toElevationM: peakElevationM,
        },
        {
          fromAlongM: alongOffsetM,
          fromElevationM: peakElevationM,
          toAlongM: alongOffsetM + halfWidthM * 0.72,
          toElevationM: rowElevationM,
        },
      );
    }
  }
  for (const segment of braceSegments) {
    addGeometry(
      wallBuckets,
      doorKey,
      doorMaterial,
      createFacadeBraceGeometry(
        edge,
        segment.fromAlongM,
        segment.fromElevationM,
        segment.toAlongM,
        segment.toElevationM,
        outwardOffsetM + 0.012,
        0.055,
        0.035,
      ),
    );
  }

  addGeometry(
    wallBuckets,
    metalKey,
    materials.streetPole,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM + widthM * 0.32,
      0.055,
      bottomElevationM + heightM * 0.52,
      0.2,
      outwardOffsetM + 0.045,
      0.065,
    ),
  );
}

function addOldTownFramedEntranceGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  trimKey: string,
  windowKey: string,
  doorKey: string,
  metalKey: string,
  portalLanternBulbKey: string,
  trimMaterial: GeometryBucket["material"],
  doorMaterial: GeometryBucket["material"],
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  baseElevationM: number,
  topElevationM: number,
  frontagePhase: number,
  hasShopCanopy: boolean,
  hasPortalLantern: boolean,
  doorPanelPattern: OldTownDoorPanelPattern,
): void {
  const requestedFrameWidthM = 1.66;
  const alongTravelM = Math.max(
    0,
    edge.lengthM - requestedFrameWidthM - 1.2,
  );
  const hostedEntrance = fitAttachedFacadePlacement({
    alongOffsetM: (frontagePhase - 0.5) * alongTravelM,
    centerY: baseElevationM + 1.34,
    widthM: requestedFrameWidthM,
    heightM: 2.68,
  }, {
    edgeLengthM: edge.lengthM,
    baseElevationM,
    topElevationM,
    horizontalAdornmentM: 0.12,
    bottomAdornmentM: 0,
    topAdornmentM: 0.12,
    minimumPanelWidthM: 1.3,
    minimumPanelHeightM: 2.3,
  });
  if (hostedEntrance === null) {
    return;
  }
  const frameWidthM = hostedEntrance.widthM;
  const alongOffsetM = hostedEntrance.alongOffsetM;
  addGeometry(
    wallBuckets,
    doorKey,
    doorMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      1.34,
      baseElevationM + 1.02,
      1.96,
      0.13,
      0.05,
    ),
  );
  const transom = fitAttachedFacadeWindowPlacement({
    alongOffsetM,
    centerY: baseElevationM + 2.25,
    widthM: 1.22,
    heightM: 0.38,
    litAtNight: false,
  }, {
    edgeLengthM: edge.lengthM,
    baseElevationM,
    topElevationM,
    horizontalAdornmentM: 0.22,
    bottomAdornmentM: 0,
    topAdornmentM: 0.25,
    minimumPanelWidthM: 0.4,
    minimumPanelHeightM: 0.24,
  });
  if (transom !== null) {
    addGeometry(
      wallBuckets,
      windowKey,
      materials.window,
      createOrientedBoxGeometry(
        edge,
        transom.alongOffsetM,
        transom.widthM,
        transom.centerY,
        transom.heightM,
        0.14,
        0.05,
      ),
    );
  }
  addOldTownDoorLeafDetailGeometry(
    wallBuckets,
    doorKey,
    metalKey,
    doorMaterial,
    materials,
    edge,
    alongOffsetM,
    1.18,
    baseElevationM + 0.08,
    1.84,
    0.19,
    doorPanelPattern,
  );
  if (hasPortalLantern) {
    addOldTownPortalLanternGeometry(
      wallBuckets,
      metalKey,
      portalLanternBulbKey,
      materials,
      edge,
      alongOffsetM,
      1.34,
      baseElevationM,
    );
  }
  for (const side of [-1, 1] as const) {
    addGeometry(
      wallBuckets,
      trimKey,
      trimMaterial,
      createOrientedBoxGeometry(
        edge,
        alongOffsetM + side * 0.75,
        0.16,
        baseElevationM + 1.34,
        2.68,
        0.16,
        0.13,
      ),
    );
  }
  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      frameWidthM,
      baseElevationM + 2.6,
      0.18,
      0.16,
      0.13,
    ),
  );
  addGeometry(
    wallBuckets,
    trimKey,
    trimMaterial,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      frameWidthM,
      baseElevationM + 0.08,
      0.16,
      0.16,
      0.13,
    ),
  );
  if (hasShopCanopy) {
    const canopy = fitAttachedFacadePlacement({
      alongOffsetM,
      centerY: baseElevationM + 2.66,
      widthM: 2.5,
      heightM: 0.1,
    }, {
      edgeLengthM: edge.lengthM,
      baseElevationM,
      topElevationM,
      horizontalAdornmentM: 0.08,
      bottomAdornmentM: 0,
      topAdornmentM: 0,
      minimumPanelWidthM: 1.4,
      minimumPanelHeightM: 0.08,
    });
    if (canopy !== null) {
      addGeometry(
        wallBuckets,
        metalKey,
        materials.streetPole,
        createOrientedBoxGeometry(
          edge,
          canopy.alongOffsetM,
          canopy.widthM,
          canopy.centerY,
          canopy.heightM,
          0.56,
          1.04,
        ),
      );
    }
  }
}

function addOldTownPortalLanternGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  metalKey: string,
  bulbKey: string,
  materials: CityMaterialLibrary,
  edge: Readonly<ReferenceFacadeEdge>,
  portalAlongOffsetM: number,
  portalWidthM: number,
  baseElevationM: number,
): void {
  const inwardSide = portalAlongOffsetM > 0 ? -1 : 1;
  const alongOffsetM =
    portalAlongOffsetM + inwardSide * (portalWidthM / 2 + 0.28);
  const bracketElevationM = baseElevationM + 2.3;
  addGeometry(
    wallBuckets,
    metalKey,
    materials.streetPole,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      0.1,
      bracketElevationM,
      0.3,
      0.22,
      0.08,
    ),
  );
  addGeometry(
    wallBuckets,
    metalKey,
    materials.streetPole,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      0.08,
      bracketElevationM + 0.04,
      0.08,
      0.36,
      0.36,
    ),
  );
  addGeometry(
    wallBuckets,
    metalKey,
    materials.streetPole,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      0.34,
      bracketElevationM - 0.08,
      0.1,
      0.52,
      0.34,
    ),
  );
  addGeometry(
    wallBuckets,
    bulbKey,
    materials.oldTownPortalLanternBulb,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      0.14,
      bracketElevationM - 0.23,
      0.16,
      0.5,
      0.14,
    ),
  );
  addGeometry(
    wallBuckets,
    metalKey,
    materials.streetPole,
    createOrientedBoxGeometry(
      edge,
      alongOffsetM,
      0.22,
      bracketElevationM - 0.36,
      0.06,
      0.5,
      0.22,
    ),
  );
}

function nearestFrontageReferencePoint(
  footprint: Readonly<PolygonWithHoles>,
  buildingCentroid: Readonly<{ x: number; z: number }>,
  segments: readonly PreparedFrontageSegment[],
  maximumStreetDistanceM = OLD_TOWN_DETAIL_MAXIMUM_STREET_DISTANCE_M,
): Readonly<{ x: number; z: number }> | null {
  const ring = normalizedWallRing(footprint.outer, false);
  const footprintBounds = ring.reduce(
    (bounds, point) => ({
      minX: Math.min(bounds.minX, point.x),
      minZ: Math.min(bounds.minZ, point.z),
      maxX: Math.max(bounds.maxX, point.x),
      maxZ: Math.max(bounds.maxZ, point.z),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      minZ: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      maxZ: Number.NEGATIVE_INFINITY,
    },
  );
  const maximumStreetDistanceSquared = maximumStreetDistanceM ** 2;
  let nearest:
    | Readonly<{ point: Readonly<{ x: number; z: number }>; distanceM: number }>
    | null = null;
  for (const segment of segments) {
    const separationX = Math.max(
      0,
      footprintBounds.minX - segment.maxX,
      segment.minX - footprintBounds.maxX,
    );
    const separationZ = Math.max(
      0,
      footprintBounds.minZ - segment.maxZ,
      segment.minZ - footprintBounds.maxZ,
    );
    if (
      separationX * separationX + separationZ * separationZ >
        maximumStreetDistanceSquared
    ) {
      continue;
    }
    const candidate = nearestFrontageReferenceCandidate(
      ring,
      buildingCentroid,
      segment.from,
      segment.to,
    );
    if (nearest === null || candidate.distanceM < nearest.distanceM) {
      nearest = candidate;
    }
  }
  return nearest !== null &&
      nearest.distanceM <= maximumStreetDistanceM
    ? nearest.point
    : null;
}

function prepareFrontageSegments(
  lines: readonly Readonly<{
    points: readonly Readonly<{ x: number; z: number }>[];
  }>[],
): readonly PreparedFrontageSegment[] {
  const segments: PreparedFrontageSegment[] = [];
  for (const line of lines) {
    for (let index = 1; index < line.points.length; index += 1) {
      const from = line.points[index - 1];
      const to = line.points[index];
      if (from === undefined || to === undefined) continue;
      segments.push({
        from,
        to,
        minX: Math.min(from.x, to.x),
        minZ: Math.min(from.z, to.z),
        maxX: Math.max(from.x, to.x),
        maxZ: Math.max(from.z, to.z),
      });
    }
  }
  return segments;
}

function nearestFrontageReferenceCandidate(
  ring: readonly Readonly<{ x: number; z: number }>[],
  buildingCentroid: Readonly<{ x: number; z: number }>,
  roadFrom: Readonly<{ x: number; z: number }>,
  roadTo: Readonly<{ x: number; z: number }>,
): Readonly<{
  point: Readonly<{ x: number; z: number }>;
  distanceM: number;
}> {
  let nearest:
    | Readonly<{ point: Readonly<{ x: number; z: number }>; distanceM: number }>
    | null = null;
  for (const footprintPoint of ring) {
    const roadPoint = closestPointOnSegment(footprintPoint, roadFrom, roadTo);
    const candidate = {
      point: roadPoint,
      distanceM: Math.hypot(
        roadPoint.x - footprintPoint.x,
        roadPoint.z - footprintPoint.z,
      ),
    };
    if (nearest === null || candidate.distanceM < nearest.distanceM) {
      nearest = candidate;
    }
  }
  for (let index = 1; index < ring.length; index += 1) {
    const footprintFrom = ring[index - 1];
    const footprintTo = ring[index];
    if (footprintFrom === undefined || footprintTo === undefined) {
      continue;
    }
    for (const roadPoint of [roadFrom, roadTo]) {
      const footprintPoint = closestPointOnSegment(
        roadPoint,
        footprintFrom,
        footprintTo,
      );
      const candidate = {
        point: roadPoint,
        distanceM: Math.hypot(
          roadPoint.x - footprintPoint.x,
          roadPoint.z - footprintPoint.z,
        ),
      };
      if (nearest === null || candidate.distanceM < nearest.distanceM) {
        nearest = candidate;
      }
    }
  }
  if (nearest !== null) {
    return nearest;
  }
  const point = closestPointOnSegment(buildingCentroid, roadFrom, roadTo);
  return {
    point,
    distanceM: Math.hypot(
      point.x - buildingCentroid.x,
      point.z - buildingCentroid.z,
    ),
  };
}

function closestPointOnSegment(
  point: Readonly<{ x: number; z: number }>,
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
): Readonly<{ x: number; z: number }> {
  const deltaX = to.x - from.x;
  const deltaZ = to.z - from.z;
  const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
  const interpolation = lengthSquared <= 0
    ? 0
    : Math.max(
        0,
        Math.min(
          1,
          (
            (point.x - from.x) * deltaX +
            (point.z - from.z) * deltaZ
          ) / lengthSquared,
        ),
      );
  return {
    x: from.x + deltaX * interpolation,
    z: from.z + deltaZ * interpolation,
  };
}

function referenceFacadeEdge(
  footprint: Readonly<PolygonWithHoles>,
  referencePoint: Readonly<{ x: number; z: number }>,
): ReferenceFacadeEdge | null {
  const ring = normalizedWallRing(footprint.outer, false);
  let best:
    | Readonly<{ edge: ReferenceFacadeEdge; distanceM: number }>
    | null = null;
  for (let index = 1; index < ring.length; index += 1) {
    const from = ring[index - 1];
    const to = ring[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM < 3) {
      continue;
    }
    const rawAlong = { x: deltaX / lengthM, z: deltaZ / lengthM };
    const midpoint = {
      x: (from.x + to.x) / 2,
      z: (from.z + to.z) / 2,
    };
    const rawOutward = { x: -rawAlong.z, z: rawAlong.x };
    const facesReference =
      (referencePoint.x - midpoint.x) * rawOutward.x +
        (referencePoint.z - midpoint.z) * rawOutward.z >= 0;
    const along = facesReference
      ? rawAlong
      : { x: -rawAlong.x, z: -rawAlong.z };
    const outward = facesReference
      ? rawOutward
      : { x: -rawOutward.x, z: -rawOutward.z };
    const distanceM = distanceToSegment(referencePoint, from, to);
    if (best === null || distanceM < best.distanceM) {
      best = {
        edge: { midpoint, along, outward, lengthM },
        distanceM,
      };
    }
  }
  if (best === null) {
    return null;
  }
  const { along, outward } = best.edge;
  let frontProjection = Number.NEGATIVE_INFINITY;
  for (const point of ring) {
    frontProjection = Math.max(
      frontProjection,
      point.x * outward.x + point.z * outward.z,
    );
  }
  let minimumAlong = Number.POSITIVE_INFINITY;
  let maximumAlong = Number.NEGATIVE_INFINITY;
  for (const point of ring) {
    const projectedOutward = point.x * outward.x + point.z * outward.z;
    if (frontProjection - projectedOutward > 1.5) {
      continue;
    }
    const projectedAlong = point.x * along.x + point.z * along.z;
    minimumAlong = Math.min(minimumAlong, projectedAlong);
    maximumAlong = Math.max(maximumAlong, projectedAlong);
  }
  const frontageLengthM = maximumAlong - minimumAlong;
  if (
    !Number.isFinite(frontageLengthM) ||
    frontageLengthM <= best.edge.lengthM + 0.5
  ) {
    return best.edge;
  }
  const midpointAlong = (minimumAlong + maximumAlong) / 2;
  return {
    midpoint: {
      x: along.x * midpointAlong + outward.x * frontProjection,
      z: along.z * midpointAlong + outward.z * frontProjection,
    },
    along,
    outward,
    lengthM: frontageLengthM,
  };
}

function distanceToSegment(
  point: Readonly<{ x: number; z: number }>,
  from: Readonly<{ x: number; z: number }>,
  to: Readonly<{ x: number; z: number }>,
): number {
  const closest = closestPointOnSegment(point, from, to);
  return Math.hypot(
    point.x - closest.x,
    point.z - closest.z,
  );
}

function createOrientedBoxGeometry(
  edge: Readonly<ReferenceFacadeEdge>,
  alongOffsetM: number,
  widthM: number,
  centerY: number,
  heightM: number,
  outwardOffsetM: number,
  depthM: number,
): BufferGeometry {
  const centerX = edge.midpoint.x +
    edge.along.x * alongOffsetM +
    edge.outward.x * outwardOffsetM;
  const centerZ = edge.midpoint.z +
    edge.along.z * alongOffsetM +
    edge.outward.z * outwardOffsetM;
  const positions = new Float32Array(ORIENTED_BOX_TEMPLATE.positions.length);
  const normals = new Float32Array(ORIENTED_BOX_TEMPLATE.normals.length);
  for (let offset = 0; offset < positions.length; offset += 3) {
    const localX = ORIENTED_BOX_TEMPLATE.positions[offset] ?? 0;
    const localY = ORIENTED_BOX_TEMPLATE.positions[offset + 1] ?? 0;
    const localZ = ORIENTED_BOX_TEMPLATE.positions[offset + 2] ?? 0;
    positions[offset] = centerX +
      edge.along.x * localX * widthM +
      edge.outward.x * localZ * depthM;
    positions[offset + 1] = centerY + localY * heightM;
    positions[offset + 2] = centerZ +
      edge.along.z * localX * widthM +
      edge.outward.z * localZ * depthM;
    const normalX = ORIENTED_BOX_TEMPLATE.normals[offset] ?? 0;
    const normalY = ORIENTED_BOX_TEMPLATE.normals[offset + 1] ?? 0;
    const normalZ = ORIENTED_BOX_TEMPLATE.normals[offset + 2] ?? 0;
    normals[offset] = edge.along.x * normalX + edge.outward.x * normalZ;
    normals[offset + 1] = normalY;
    normals[offset + 2] = edge.along.z * normalX + edge.outward.z * normalZ;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new BufferAttribute(normals, 3));
  geometry.setAttribute(
    "uv",
    new BufferAttribute(ORIENTED_BOX_TEMPLATE.uvs, 2),
  );
  geometry.setIndex(new BufferAttribute(ORIENTED_BOX_TEMPLATE.indices, 1));
  return geometry;
}

function createFacadeRoofApronGeometry(
  edge: Readonly<ReferenceFacadeEdge>,
  widthM: number,
  eaveElevationM: number,
  topElevationM: number,
  runM: number,
): BufferGeometry {
  const inward = { x: -edge.outward.x, z: -edge.outward.z };
  const point = (
    alongOffsetM: number,
    inwardOffsetM: number,
    elevationM: number,
  ): readonly [number, number, number] => [
    edge.midpoint.x +
      edge.along.x * alongOffsetM + inward.x * inwardOffsetM,
    elevationM,
    edge.midpoint.z +
      edge.along.z * alongOffsetM + inward.z * inwardOffsetM,
  ];
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute([
      ...point(-widthM / 2, 0, eaveElevationM),
      ...point(widthM / 2, 0, eaveElevationM),
      ...point(widthM / 2, runM, topElevationM),
      ...point(-widthM / 2, runM, topElevationM),
    ], 3),
  );
  geometry.setAttribute(
    "uv",
    new Float32BufferAttribute([
      0, 0,
      widthM / 2.4, 0,
      widthM / 2.4, runM / 2.4,
      0, runM / 2.4,
    ], 2),
  );
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createRoofSurfaceBoxGeometry(
  surface: Readonly<RoofWindowSurface>,
  alongM: number,
  slopeProgress: number,
  widthM: number,
  slopeHeightM: number,
  thicknessM: number,
  surfaceOffsetM: number,
): BufferGeometry {
  const progressMargin = slopeHeightM / surface.slopeLengthM / 2;
  const progress = Math.max(
    progressMargin + 0.05,
    Math.min(0.95 - progressMargin, slopeProgress),
  );
  let alongAxis = new Vector3(
    surface.along.x,
    0,
    surface.along.z,
  ).normalize();
  const riseM = surface.topElevationM - surface.eaveElevationM;
  const slopeAxis = new Vector3(
    surface.inward.x * surface.runM,
    riseM,
    surface.inward.z * surface.runM,
  ).normalize();
  let normal = new Vector3().crossVectors(alongAxis, slopeAxis).normalize();
  if (normal.y < 0) {
    alongAxis = alongAxis.negate();
    normal = normal.negate();
  }
  const center = new Vector3(
    surface.along.x * alongM +
      surface.inward.x *
        (surface.eaveAcrossM + surface.runM * progress),
    surface.eaveElevationM + riseM * progress,
    surface.along.z * alongM +
      surface.inward.z *
        (surface.eaveAcrossM + surface.runM * progress),
  ).addScaledVector(normal, surfaceOffsetM + thicknessM / 2);
  const geometry = new BoxGeometry(widthM, slopeHeightM, thicknessM);
  const basis = new Matrix4().makeBasis(alongAxis, slopeAxis, normal);
  basis.setPosition(center);
  geometry.applyMatrix4(basis);
  return geometry;
}

function createFacadeBraceGeometry(
  edge: Readonly<ReferenceFacadeEdge>,
  fromAlongM: number,
  fromElevationM: number,
  toAlongM: number,
  toElevationM: number,
  outwardOffsetM: number,
  thicknessM: number,
  depthM: number,
): BufferGeometry {
  const alongDeltaM = toAlongM - fromAlongM;
  const elevationDeltaM = toElevationM - fromElevationM;
  const lengthM = Math.hypot(alongDeltaM, elevationDeltaM);
  const geometry = new BoxGeometry(lengthM, thicknessM, depthM);
  geometry.rotateZ(Math.atan2(elevationDeltaM, alongDeltaM));
  const basis = new Matrix4().makeBasis(
    new Vector3(edge.along.x, 0, edge.along.z),
    new Vector3(0, 1, 0),
    new Vector3(edge.outward.x, 0, edge.outward.z),
  );
  basis.setPosition(
    edge.midpoint.x +
      edge.along.x * (fromAlongM + toAlongM) / 2 +
      edge.outward.x * outwardOffsetM,
    (fromElevationM + toElevationM) / 2,
    edge.midpoint.z +
      edge.along.z * (fromAlongM + toAlongM) / 2 +
      edge.outward.z * outwardOffsetM,
  );
  geometry.applyMatrix4(basis);
  return geometry;
}

function createArchGeometry(
  edge: Readonly<ReferenceFacadeEdge>,
  alongOffsetM: number,
  widthM: number,
  heightM: number,
  bottomElevationM: number,
  outwardOffsetM: number,
): BufferGeometry {
  const radiusM = widthM / 2;
  const springElevationM = bottomElevationM + heightM - radiusM;
  const perimeter: { alongM: number; y: number }[] = [
    { alongM: -radiusM, y: bottomElevationM },
    { alongM: radiusM, y: bottomElevationM },
    { alongM: radiusM, y: springElevationM },
  ];
  for (let segment = 1; segment <= 8; segment += 1) {
    const angle = Math.PI * segment / 8;
    perimeter.push({
      alongM: Math.cos(angle) * radiusM,
      y: springElevationM + Math.sin(angle) * radiusM,
    });
  }
  const center = {
    alongM: 0,
    y: bottomElevationM + heightM * 0.46,
  };
  const positions: number[] = [];
  const uvs: number[] = [];
  const append = (point: Readonly<{ alongM: number; y: number }>): void => {
    positions.push(
      edge.midpoint.x +
        edge.along.x * (alongOffsetM + point.alongM) +
        edge.outward.x * outwardOffsetM,
      point.y,
      edge.midpoint.z +
        edge.along.z * (alongOffsetM + point.alongM) +
        edge.outward.z * outwardOffsetM,
    );
    uvs.push(
      0.5 + point.alongM / widthM,
      (point.y - bottomElevationM) / heightM,
    );
  };
  for (let index = 0; index < perimeter.length; index += 1) {
    append(center);
    append(perimeter[index]!);
    append(perimeter[(index + 1) % perimeter.length]!);
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geometry.computeVertexNormals();
  return geometry;
}

function createPedimentGeometry(
  edge: Readonly<ReferenceFacadeEdge>,
  alongOffsetM: number,
  widthM: number,
  baseElevationM: number,
  apexElevationM: number,
  outwardOffsetM: number,
): BufferGeometry {
  const point = (
    offsetM: number,
    elevationM: number,
  ): readonly [number, number, number] => [
    edge.midpoint.x +
      edge.along.x * (alongOffsetM + offsetM) +
      edge.outward.x * outwardOffsetM,
    elevationM,
    edge.midpoint.z +
      edge.along.z * (alongOffsetM + offsetM) +
      edge.outward.z * outwardOffsetM,
  ];
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute([
      ...point(-widthM / 2, baseElevationM),
      ...point(widthM / 2, baseElevationM),
      ...point(0, apexElevationM),
    ], 3),
  );
  geometry.setAttribute(
    "uv",
    new Float32BufferAttribute([0, 0, 1, 0, 0.5, 1], 2),
  );
  geometry.computeVertexNormals();
  return geometry;
}

function clampInteger(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, Math.round(value)));
}

interface RoofProfile {
  readonly eaveElevationM: number;
  readonly topElevationM: number;
  readonly frame: RoofFrame | null;
  readonly config: Readonly<BuildingRenderConfig>;
}

interface RoofFrame {
  readonly along: Readonly<{ x: number; z: number }>;
  readonly across: Readonly<{ x: number; z: number }>;
  readonly minAlong: number;
  readonly maxAlong: number;
  readonly minAcross: number;
  readonly maxAcross: number;
}

interface RoofWindowSurface {
  readonly along: Readonly<{ x: number; z: number }>;
  readonly inward: Readonly<{ x: number; z: number }>;
  readonly minAlongM: number;
  readonly maxAlongM: number;
  readonly eaveAcrossM: number;
  readonly runM: number;
  readonly eaveElevationM: number;
  readonly topElevationM: number;
  readonly slopeLengthM: number;
}

function roofWindowSurfaceForProfile(
  profile: Readonly<RoofProfile>,
  roofType: "gable" | "hip",
  edge: Readonly<ReferenceFacadeEdge>,
): RoofWindowSurface | null {
  const frame = profile.frame;
  if (frame === null) {
    return null;
  }
  const acrossSpanM = frame.maxAcross - frame.minAcross;
  const alongSpanM = frame.maxAlong - frame.minAlong;
  const riseM = profile.topElevationM - profile.eaveElevationM;
  if (acrossSpanM < 1.2 || alongSpanM < 2 || riseM < 0.2) {
    return null;
  }
  const facadeAcrossM =
    edge.midpoint.x * frame.across.x +
    edge.midpoint.z * frame.across.z;
  const useMinimumSide =
    Math.abs(facadeAcrossM - frame.minAcross) <=
    Math.abs(facadeAcrossM - frame.maxAcross);
  const sideSign = useMinimumSide ? 1 : -1;
  const runM = acrossSpanM / 2;
  const hipInsetM = roofType === "hip"
    ? Math.min(
        alongSpanM * profile.config.hipRidgeInsetRatio,
        acrossSpanM * 0.5,
      )
    : 0;
  const minAlongM = frame.minAlong + hipInsetM;
  const maxAlongM = frame.maxAlong - hipInsetM;
  if (maxAlongM - minAlongM < OLD_TOWN_ROOF_WINDOW_WIDTH_M + 0.8) {
    return null;
  }
  return {
    along: frame.along,
    inward: {
      x: frame.across.x * sideSign,
      z: frame.across.z * sideSign,
    },
    minAlongM,
    maxAlongM,
    eaveAcrossM: useMinimumSide
      ? frame.minAcross
      : -frame.maxAcross,
    runM,
    eaveElevationM: profile.eaveElevationM,
    topElevationM: profile.topElevationM,
    slopeLengthM: Math.hypot(runM, riseM),
  };
}

function roofWindowSurfaceForFacadeApron(
  edge: Readonly<ReferenceFacadeEdge>,
  widthM: number,
  eaveElevationM: number,
  topElevationM: number,
  runM: number,
): RoofWindowSurface {
  const inward = { x: -edge.outward.x, z: -edge.outward.z };
  const centerAlongM =
    edge.midpoint.x * edge.along.x +
    edge.midpoint.z * edge.along.z;
  const eaveAcrossM =
    edge.midpoint.x * inward.x + edge.midpoint.z * inward.z;
  return {
    along: edge.along,
    inward,
    minAlongM: centerAlongM - widthM / 2,
    maxAlongM: centerAlongM + widthM / 2,
    eaveAcrossM,
    runM,
    eaveElevationM,
    topElevationM,
    slopeLengthM: Math.hypot(runM, topElevationM - eaveElevationM),
  };
}

function roofProfileFor(
  footprint: Readonly<PolygonWithHoles>,
  spec: Readonly<VilniusBuildingVisualSpec>,
  config: Readonly<BuildingRenderConfig>,
  heightScale: number,
): RoofProfile {
  const totalHeight = spec.height.metres * heightScale;
  const topElevationM = spec.baseElevationM + totalHeight;
  const frame = spec.roof.type === "flat"
    ? null
    : rectangularRoofFrame(footprint, config, spec.roofOrientation);
  if (frame === null) {
    return {
      eaveElevationM: topElevationM,
      topElevationM,
      frame: null,
      config,
    };
  }
  const shortSpan = Math.min(
    frame.maxAlong - frame.minAlong,
    frame.maxAcross - frame.minAcross,
  );
  const pitchRise = spec.roofRiseM === undefined
    ? Math.tan(spec.roof.pitchDegrees * Math.PI / 180) * shortSpan / 2
    : spec.roofRiseM * heightScale;
  const rise = Math.min(
    config.oldTownMaximumRoofRiseM,
    totalHeight * config.oldTownMaximumRoofHeightRatio,
    Math.max(
      spec.roofRiseM === undefined ? config.oldTownMinimumRoofRiseM : 0.15,
      pitchRise,
    ),
  );
  return {
    eaveElevationM: topElevationM - rise,
    topElevationM,
    frame,
    config,
  };
}

function rectangularRoofFrame(
  footprint: Readonly<PolygonWithHoles>,
  config: Readonly<BuildingRenderConfig>,
  roofOrientation: "along" | "across" | undefined,
): RoofFrame | null {
  if (
    footprint.holes.length > 0 ||
    footprint.outer.length < 4 ||
    !isConvexRoofRing(footprint.outer)
  ) {
    return null;
  }
  let longestEdge = {
    x: 1,
    z: 0,
    lengthM: 0,
  };
  for (let index = 1; index < footprint.outer.length; index += 1) {
    const from = footprint.outer[index - 1];
    const to = footprint.outer[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const x = to.x - from.x;
    const z = to.z - from.z;
    const lengthM = Math.hypot(x, z);
    if (lengthM > longestEdge.lengthM) {
      longestEdge = { x, z, lengthM };
    }
  }
  if (longestEdge.lengthM <= 0.001) {
    return null;
  }
  const longestAxis = {
    x: longestEdge.x / longestEdge.lengthM,
    z: longestEdge.z / longestEdge.lengthM,
  };
  const perpendicularAxis = { x: -longestAxis.z, z: longestAxis.x };
  const along = roofOrientation === "across"
    ? perpendicularAxis
    : longestAxis;
  const across = { x: -along.z, z: along.x };
  let minAlong = Number.POSITIVE_INFINITY;
  let maxAlong = Number.NEGATIVE_INFINITY;
  let minAcross = Number.POSITIVE_INFINITY;
  let maxAcross = Number.NEGATIVE_INFINITY;
  for (const point of footprint.outer) {
    const projectedAlong = point.x * along.x + point.z * along.z;
    const projectedAcross = point.x * across.x + point.z * across.z;
    minAlong = Math.min(minAlong, projectedAlong);
    maxAlong = Math.max(maxAlong, projectedAlong);
    minAcross = Math.min(minAcross, projectedAcross);
    maxAcross = Math.max(maxAcross, projectedAcross);
  }
  const boxAreaM2 =
    (maxAlong - minAlong) * (maxAcross - minAcross);
  const footprintAreaM2 = Math.abs(signedRingAreaM2(footprint.outer));
  if (
    boxAreaM2 <= 0.01 ||
    footprintAreaM2 / boxAreaM2 <
      config.roofMinimumRectangularityRatio
  ) {
    return null;
  }
  const overhang = config.roofEaveOverhangM;
  return {
    along,
    across,
    minAlong: minAlong - overhang,
    maxAlong: maxAlong + overhang,
    minAcross: minAcross - overhang,
    maxAcross: maxAcross + overhang,
  };
}

function isConvexRoofRing(ring: Readonly<LinearRing>): boolean {
  const pointCount = isClosedRing(ring) ? ring.length - 1 : ring.length;
  if (pointCount < 3) {
    return false;
  }
  let winding = 0;
  for (let index = 0; index < pointCount; index += 1) {
    const previous = ring[(index + pointCount - 1) % pointCount];
    const current = ring[index];
    const next = ring[(index + 1) % pointCount];
    if (previous === undefined || current === undefined || next === undefined) {
      return false;
    }
    const cross =
      (current.x - previous.x) * (next.z - current.z) -
      (current.z - previous.z) * (next.x - current.x);
    if (Math.abs(cross) <= 0.000_001) {
      continue;
    }
    const direction = Math.sign(cross);
    if (winding !== 0 && direction !== winding) {
      return false;
    }
    winding = direction;
  }
  return winding !== 0;
}

function footprintBounds(
  footprint: Readonly<PolygonWithHoles>,
): {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
} {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const point of footprint.outer) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minZ = Math.min(minZ, point.z);
    maxZ = Math.max(maxZ, point.z);
  }
  return { minX, maxX, minZ, maxZ };
}

function createFacadeGeometry(
  footprint: Readonly<PolygonWithHoles>,
  baseElevationM: number,
  eaveElevationM: number,
  spec: Readonly<VilniusBuildingVisualSpec>,
  passages: readonly Readonly<AuthoredBuildingPassageSpec>[],
  includesRing: (holeIndex: number | null) => boolean = () => true,
): BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const bounds = footprintBounds(footprint);
  const longestSpan = Math.max(
    bounds.maxX - bounds.minX,
    bounds.maxZ - bounds.minZ,
  );
  const bayWidthM = longestSpan / Math.max(1, spec.facade.bayCount);
  const heightM = eaveElevationM - baseElevationM;
  const rowCount = Math.max(1, spec.facade.windowRhythm.rowCount);

  const appendRing = (
    source: Readonly<LinearRing>,
    holeIndex: number | null,
  ): void => {
    const hole = holeIndex !== null;
    const ring = normalizedWallRing(source, hole);
    for (let index = 1; index < ring.length; index += 1) {
      const from = ring[index - 1];
      const to = ring[index];
      if (from === undefined || to === undefined) {
        continue;
      }
      const edgeLengthM = Math.hypot(to.x - from.x, to.z - from.z);
      const openings = wallOpeningIntervalsForPassages(
        from,
        to,
        passagesOpeningFootprintRing(passages, holeIndex),
      );
      const appendWallSection = (
        startM: number,
        endM: number,
        bottomElevationM: number,
        topElevationM: number,
      ): void => {
        if (
          endM - startM <= 0.01 ||
          topElevationM - bottomElevationM <= 0.01
        ) {
          return;
        }
        const startRatio = startM / edgeLengthM;
        const endRatio = endM / edgeLengthM;
        const start = {
          x: from.x + (to.x - from.x) * startRatio,
          z: from.z + (to.z - from.z) * startRatio,
        };
        const end = {
          x: from.x + (to.x - from.x) * endRatio,
          z: from.z + (to.z - from.z) * endRatio,
        };
        const vertexOffset = positions.length / 3;
        positions.push(
          start.x, bottomElevationM, start.z,
          start.x, topElevationM, start.z,
          end.x, topElevationM, end.z,
          end.x, bottomElevationM, end.z,
        );
        const startU = startM / bayWidthM;
        const endU = endM / bayWidthM;
        const bottomV =
          (bottomElevationM - baseElevationM) / heightM * rowCount;
        const topV =
          (topElevationM - baseElevationM) / heightM * rowCount;
        uvs.push(
          startU, bottomV,
          startU, topV,
          endU, topV,
          endU, bottomV,
        );
        indices.push(
          vertexOffset,
          vertexOffset + 1,
          vertexOffset + 2,
          vertexOffset,
          vertexOffset + 2,
          vertexOffset + 3,
        );
      };
      if (openings.length === 0) {
        appendWallSection(
          0,
          edgeLengthM,
          baseElevationM,
          eaveElevationM,
        );
        continue;
      }
      let cursorM = 0;
      for (const opening of openings) {
        appendWallSection(
          cursorM,
          opening.startM,
          baseElevationM,
          eaveElevationM,
        );
        appendWallSection(
          opening.startM,
          opening.endM,
          baseElevationM +
            opening.ceilingOffsetFromBuildingBaseM,
          eaveElevationM,
        );
        cursorM = opening.endM;
      }
      appendWallSection(
        cursorM,
        edgeLengthM,
        baseElevationM,
        eaveElevationM,
      );
    }
  };
  if (includesRing(null)) {
    appendRing(footprint.outer, null);
  }
  for (const [holeIndex, hole] of footprint.holes.entries()) {
    if (includesRing(holeIndex)) {
      appendRing(hole, holeIndex);
    }
  }
  if (positions.length === 0 || heightM <= 0) {
    throw new Error(`Building ${spec.buildingId} has no renderable facade`);
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function addBuildingPassageLiningGeometry(
  wallBuckets: Map<string, GeometryBucket>,
  materials: CityMaterialLibrary,
  footprint: Readonly<PolygonWithHoles>,
  spec: Readonly<VilniusBuildingVisualSpec>,
  passages: readonly Readonly<AuthoredBuildingPassageSpec>[],
): void {
  for (const passage of passages) {
    const span = buildingPassageSpanWithinFootprint(footprint, passage);
    if (span === null) {
      continue;
    }
    const direction = {
      x: (span.to.x - span.from.x) / span.lengthM,
      z: (span.to.z - span.from.z) / span.lengthM,
    };
    const lateral = { x: -direction.z, z: direction.x };
    const midpoint = {
      x: (span.from.x + span.to.x) / 2,
      z: (span.from.z + span.to.z) / 2,
    };
    const ceilingElevationM =
      spec.baseElevationM + passage.ceilingOffsetFromBuildingBaseM;
    const liningHeightM =
      passage.ceilingOffsetFromBuildingBaseM +
      BUILDING_PASSAGE_LINING_DEPTH_BELOW_BASE_M;
    const basis = new Matrix4().makeBasis(
      new Vector3(lateral.x, 0, lateral.z),
      new Vector3(0, 1, 0),
      new Vector3(direction.x, 0, direction.z),
    );
    const addBox = (
      key: string,
      material: GeometryBucket["material"],
      widthM: number,
      heightM: number,
      depthM: number,
      centerX: number,
      centerY: number,
      centerZ: number,
    ): void => {
      const geometry = new BoxGeometry(widthM, heightM, depthM);
      const transform = basis.clone();
      transform.setPosition(centerX, centerY, centerZ);
      geometry.applyMatrix4(transform);
      addGeometry(
        wallBuckets,
        key,
        material,
        geometry,
      );
    };
    const isGelezinioVilkoTunnel = passage.id.startsWith(
      GELEZINIO_VILKO_TUNNEL_ART.passageIdPrefix,
    );
    const liningMaterial = isGelezinioVilkoTunnel
      ? materials.tunnelConcrete
      : materials.buildingPassageLining;
    const liningKey = `building-passage-lining:${passage.id}`;
    addBox(
      liningKey,
      liningMaterial,
      passage.widthM + BUILDING_PASSAGE_LINING_THICKNESS_M * 2,
      BUILDING_PASSAGE_LINING_THICKNESS_M,
      span.lengthM + BUILDING_PASSAGE_LINING_THICKNESS_M,
      midpoint.x,
      ceilingElevationM + BUILDING_PASSAGE_LINING_THICKNESS_M / 2,
      midpoint.z,
    );
    for (const side of [-1, 1] as const) {
      const lateralOffsetM =
        side *
        (passage.widthM / 2 +
          BUILDING_PASSAGE_LINING_THICKNESS_M / 2);
      addBox(
        liningKey,
        liningMaterial,
        BUILDING_PASSAGE_LINING_THICKNESS_M,
        liningHeightM,
        span.lengthM + BUILDING_PASSAGE_LINING_THICKNESS_M,
        midpoint.x + lateral.x * lateralOffsetM,
        spec.baseElevationM -
          BUILDING_PASSAGE_LINING_DEPTH_BELOW_BASE_M +
          liningHeightM / 2,
        midpoint.z + lateral.z * lateralOffsetM,
      );
    }
    if (!isGelezinioVilkoTunnel) {
      continue;
    }

    const art = GELEZINIO_VILKO_TUNNEL_ART;
    const roadElevationM = spec.baseElevationM;
    const clearHeightM = passage.ceilingOffsetFromBuildingBaseM;
    const detailDepthM = Math.max(0.5, span.lengthM - 0.8);

    // A substantial portal frame makes the two openings read as engineered
    // tunnel mouths instead of holes punched through a procedural facade.
    for (const end of [-1, 1] as const) {
      const alongM = end * (span.lengthM + art.portalFrameDepthM) / 2;
      const frameCentre = {
        x: midpoint.x + direction.x * alongM,
        z: midpoint.z + direction.z * alongM,
      };
      addBox(
        `gelezinio-tunnel-portal:${passage.id}`,
        materials.tunnelConcrete,
        passage.widthM + art.portalPierWidthM * 2,
        art.portalLintelHeightM,
        art.portalFrameDepthM,
        frameCentre.x,
        ceilingElevationM - art.portalLintelHeightM / 2,
        frameCentre.z,
      );
      for (const side of [-1, 1] as const) {
        const lateralM = side *
          (passage.widthM / 2 + art.portalPierWidthM / 2);
        addBox(
          `gelezinio-tunnel-portal:${passage.id}`,
          materials.tunnelConcrete,
          art.portalPierWidthM,
          clearHeightM - art.portalLintelHeightM,
          art.portalFrameDepthM,
          frameCentre.x + lateral.x * lateralM,
          roadElevationM + (clearHeightM - art.portalLintelHeightM) / 2,
          frameCentre.z + lateral.z * lateralM,
        );
      }
    }

    // Repeated precast joints and a darker lower band reproduce the official
    // repair-study photographs without resorting to photographic textures.
    const panelCount = Math.max(
      1,
      Math.floor(span.lengthM / art.wallPanelLengthM),
    );
    for (let panel = 1; panel < panelCount; panel += 1) {
      const alongM = -span.lengthM / 2 +
        panel * span.lengthM / panelCount;
      for (const side of [-1, 1] as const) {
        const lateralM = side * (passage.widthM / 2 - 0.012);
        addBox(
          `gelezinio-tunnel-panel-joints:${passage.id}`,
          materials.tunnelJoint,
          art.wallJointDepthM,
          clearHeightM - 0.36,
          art.wallJointWidthM,
          midpoint.x + direction.x * alongM + lateral.x * lateralM,
          roadElevationM + (clearHeightM - 0.36) / 2,
          midpoint.z + direction.z * alongM + lateral.z * lateralM,
        );
      }
    }
    for (const side of [-1, 1] as const) {
      const lateralM = side * (passage.widthM / 2 - 0.014);
      addBox(
        `gelezinio-tunnel-horizontal-joints:${passage.id}`,
        materials.tunnelJoint,
        art.wallJointDepthM,
        art.horizontalJointWidthM,
        detailDepthM,
        midpoint.x + lateral.x * lateralM,
        roadElevationM + art.horizontalJointHeightM,
        midpoint.z + lateral.z * lateralM,
      );
      addBox(
        `gelezinio-tunnel-damp-band:${passage.id}`,
        materials.tunnelDamp,
        art.wallJointDepthM * 1.4,
        art.dampBandHeightM,
        detailDepthM,
        midpoint.x + lateral.x * lateralM,
        roadElevationM + art.dampBandHeightM / 2,
        midpoint.z + lateral.z * lateralM,
      );
      const curbLateralM = side *
        (passage.widthM / 2 - art.drainageCurbWidthM / 2);
      addBox(
        `gelezinio-tunnel-drainage-curb:${passage.id}`,
        materials.tunnelConcrete,
        art.drainageCurbWidthM,
        art.drainageCurbHeightM,
        detailDepthM,
        midpoint.x + lateral.x * curbLateralM,
        roadElevationM + art.drainageCurbHeightM / 2,
        midpoint.z + lateral.z * curbLateralM,
      );
    }

    // Longitudinal soffit beams, cable trays and warm luminaires are the most
    // distinctive close-range cues in the municipality's tunnel photograph.
    const beamCount = Math.max(
      3,
      Math.floor(passage.widthM / art.soffitBeamSpacingM),
    );
    for (let beam = 0; beam < beamCount; beam += 1) {
      const lateralM = beamCount === 1
        ? 0
        : -passage.widthM / 2 + 0.45 +
          beam * (passage.widthM - 0.9) / (beamCount - 1);
      addBox(
        `gelezinio-tunnel-soffit-beams:${passage.id}`,
        materials.tunnelJoint,
        art.soffitBeamWidthM,
        art.soffitBeamDropM,
        detailDepthM,
        midpoint.x + lateral.x * lateralM,
        ceilingElevationM - art.soffitBeamDropM / 2,
        midpoint.z + lateral.z * lateralM,
      );
    }
    for (const side of [-1, 1] as const) {
      const lateralM = side * (passage.widthM / 2 - 0.11);
      addBox(
        `gelezinio-tunnel-cable-trays:${passage.id}`,
        materials.tunnelMetal,
        art.cableTrayWidthM,
        art.cableTrayHeightM,
        detailDepthM,
        midpoint.x + lateral.x * lateralM,
        ceilingElevationM - art.cableTrayDropM,
        midpoint.z + lateral.z * lateralM,
      );
    }
    const luminaireCount = Math.max(
      2,
      Math.floor(span.lengthM / art.luminaireSpacingM),
    );
    for (let light = 0; light < luminaireCount; light += 1) {
      const alongM = -span.lengthM / 2 +
        (light + 0.5) * span.lengthM / luminaireCount;
      for (const side of [-1, 1] as const) {
        const lateralM = side * (passage.widthM / 2 - 0.035);
        addBox(
          `gelezinio-tunnel-luminaires:${passage.id}`,
          materials.tunnelLight,
          art.luminaireWidthM,
          art.luminaireHeightM,
          art.luminaireLengthM,
          midpoint.x + direction.x * alongM + lateral.x * lateralM,
          ceilingElevationM - art.luminaireDropM,
          midpoint.z + direction.z * alongM + lateral.z * lateralM,
        );
      }
    }
  }
}

function normalizedWallRing(
  source: Readonly<LinearRing>,
  hole: boolean,
): LinearRing {
  const closed = source[0]?.x === source.at(-1)?.x &&
    source[0]?.z === source.at(-1)?.z
    ? [...source]
    : [...source, source[0]!];
  const shouldBePositive = !hole;
  const positive = signedRingAreaM2(closed) > 0;
  return positive === shouldBePositive ? closed : [...closed].reverse();
}

function createRoofGeometry(
  footprint: Readonly<PolygonWithHoles>,
  profile: Readonly<RoofProfile>,
  spec: Readonly<VilniusBuildingVisualSpec>,
): BufferGeometry {
  if (profile.frame !== null) {
    return createPitchedRoofGeometry(profile, spec);
  }
  const geometry = new ShapeGeometry(createPolygonShape(footprint), 1);
  geometry.rotateX(-Math.PI / 2);
  const positions = geometry.getAttribute("position");
  for (let index = 0; index < positions.count; index += 1) {
    positions.setY(index, profile.topElevationM);
  }
  positions.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function createPitchedRoofEndGeometry(
  profile: Readonly<RoofProfile>,
  roofType: "gable" | "mansard",
): BufferGeometry {
  const frame = profile.frame;
  if (frame === null) {
    throw new Error("Pitched roof end geometry requires an oriented roof frame");
  }
  const overhangM = profile.config.roofEaveOverhangM;
  const minAlong = frame.minAlong + overhangM;
  const maxAlong = frame.maxAlong - overhangM;
  const minAcross = frame.minAcross + overhangM;
  const maxAcross = frame.maxAcross - overhangM;
  const midAcross = (minAcross + maxAcross) / 2;
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const point = (
    alongM: number,
    acrossM: number,
    elevationM: number,
  ): Readonly<{ x: number; y: number; z: number }> => ({
    x: frame.along.x * alongM + frame.across.x * acrossM,
    y: elevationM,
    z: frame.along.z * alongM + frame.across.z * acrossM,
  });
  const appendTriangle = (
    first: Readonly<{ x: number; y: number; z: number }>,
    second: Readonly<{ x: number; y: number; z: number }>,
    third: Readonly<{ x: number; y: number; z: number }>,
    desired: Readonly<{ x: number; z: number }>,
  ): void => {
    const ab = {
      x: second.x - first.x,
      y: second.y - first.y,
      z: second.z - first.z,
    };
    const ac = {
      x: third.x - first.x,
      y: third.y - first.y,
      z: third.z - first.z,
    };
    const normal = {
      x: ab.y * ac.z - ab.z * ac.y,
      z: ab.x * ac.y - ab.y * ac.x,
    };
    const ordered = normal.x * desired.x + normal.z * desired.z >= 0
      ? [first, second, third]
      : [first, third, second];
    const start = positions.length / 3;
    for (const [index, vertex] of ordered.entries()) {
      positions.push(vertex.x, vertex.y, vertex.z);
      uvs.push(index === 0 ? 0 : index === 1 ? 1 : 0.5, index === 2 ? 1 : 0);
    }
    indices.push(start, start + 1, start + 2);
  };
  if (roofType === "mansard") {
    const acrossSpanM = maxAcross - minAcross;
    const breakInsetM = acrossSpanM * profile.config.mansardBreakRatio;
    const breakElevationM = profile.eaveElevationM +
      (profile.topElevationM - profile.eaveElevationM) *
        profile.config.mansardBreakHeightRatio;
    for (const [alongM, direction] of [
      [minAlong, -1],
      [maxAlong, 1],
    ] as const) {
      const desired = {
        x: frame.along.x * direction,
        z: frame.along.z * direction,
      };
      const lowerNear = point(
        alongM,
        minAcross,
        profile.eaveElevationM,
      );
      const breakNear = point(
        alongM,
        minAcross + breakInsetM,
        breakElevationM,
      );
      const ridge = point(
        alongM,
        midAcross,
        profile.topElevationM,
      );
      const breakFar = point(
        alongM,
        maxAcross - breakInsetM,
        breakElevationM,
      );
      const lowerFar = point(
        alongM,
        maxAcross,
        profile.eaveElevationM,
      );
      appendTriangle(lowerNear, breakNear, ridge, desired);
      appendTriangle(lowerNear, ridge, lowerFar, desired);
      appendTriangle(lowerFar, ridge, breakFar, desired);
    }
  } else {
    appendTriangle(
      point(minAlong, minAcross, profile.eaveElevationM),
      point(minAlong, maxAcross, profile.eaveElevationM),
      point(minAlong, midAcross, profile.topElevationM),
      { x: -frame.along.x, z: -frame.along.z },
    );
    appendTriangle(
      point(maxAlong, minAcross, profile.eaveElevationM),
      point(maxAlong, midAcross, profile.topElevationM),
      point(maxAlong, maxAcross, profile.eaveElevationM),
      { x: frame.along.x, z: frame.along.z },
    );
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

interface RoofVertex {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly u: number;
  readonly v: number;
}

function createPitchedRoofGeometry(
  profile: Readonly<RoofProfile>,
  spec: Readonly<VilniusBuildingVisualSpec>,
): BufferGeometry {
  const frame = profile.frame;
  if (frame === null) {
    throw new Error("Pitched roof geometry requires an oriented roof frame");
  }
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const minA = frame.minAlong;
  const maxA = frame.maxAlong;
  const minB = frame.minAcross;
  const maxB = frame.maxAcross;
  const midB = (minB + maxB) / 2;
  const alongSpan = maxA - minA;
  const acrossSpan = maxB - minB;
  const eave = profile.eaveElevationM;
  const top = profile.topElevationM;
  const vertex = (a: number, b: number, y: number): RoofVertex => ({
    x: frame.along.x * a + frame.across.x * b,
    y,
    z: frame.along.z * a + frame.across.z * b,
    u: (a - minA) / 2.4,
    v: (b - minB) / 2.4,
  });
  const appendTriangle = (
    first: RoofVertex,
    second: RoofVertex,
    third: RoofVertex,
    desired: Readonly<{ x: number; y: number; z: number }>,
  ): void => {
    const ab = {
      x: second.x - first.x,
      y: second.y - first.y,
      z: second.z - first.z,
    };
    const ac = {
      x: third.x - first.x,
      y: third.y - first.y,
      z: third.z - first.z,
    };
    const normal = {
      x: ab.y * ac.z - ab.z * ac.y,
      y: ab.z * ac.x - ab.x * ac.z,
      z: ab.x * ac.y - ab.y * ac.x,
    };
    const ordered = normal.x * desired.x +
          normal.y * desired.y +
          normal.z * desired.z >= 0
      ? [first, second, third]
      : [first, third, second];
    const start = positions.length / 3;
    for (const point of ordered) {
      positions.push(point.x, point.y, point.z);
      uvs.push(point.u, point.v);
    }
    indices.push(start, start + 1, start + 2);
  };
  const appendQuad = (
    a: RoofVertex,
    b: RoofVertex,
    c: RoofVertex,
    d: RoofVertex,
    desired: Readonly<{ x: number; y: number; z: number }>,
  ): void => {
    appendTriangle(a, b, c, desired);
    appendTriangle(a, c, d, desired);
  };
  const up = { x: 0, y: 1, z: 0 };
  const outwardStart = {
    x: -frame.along.x,
    y: 0,
    z: -frame.along.z,
  };
  const outwardEnd = {
    x: frame.along.x,
    y: 0,
    z: frame.along.z,
  };
  const p00 = vertex(minA, minB, eave);
  const p10 = vertex(maxA, minB, eave);
  const p11 = vertex(maxA, maxB, eave);
  const p01 = vertex(minA, maxB, eave);
  const ridgeStart = vertex(minA, midB, top);
  const ridgeEnd = vertex(maxA, midB, top);

  if (spec.roof.type === "hip") {
    const inset = Math.min(
      alongSpan * profile.config.hipRidgeInsetRatio,
      acrossSpan * 0.5,
    );
    const hipStart = vertex(minA + inset, midB, top);
    const hipEnd = vertex(maxA - inset, midB, top);
    appendQuad(p00, p10, hipEnd, hipStart, up);
    appendQuad(p01, hipStart, hipEnd, p11, up);
    appendTriangle(p00, hipStart, p01, up);
    appendTriangle(p10, p11, hipEnd, up);
  } else if (spec.roof.type === "mansard") {
    const breakInset = acrossSpan * profile.config.mansardBreakRatio;
    const breakY = eave +
      (top - eave) * profile.config.mansardBreakHeightRatio;
    const q00 = vertex(minA, minB + breakInset, breakY);
    const q10 = vertex(maxA, minB + breakInset, breakY);
    const q11 = vertex(maxA, maxB - breakInset, breakY);
    const q01 = vertex(minA, maxB - breakInset, breakY);
    appendQuad(p00, p10, q10, q00, up);
    appendQuad(q00, q10, ridgeEnd, ridgeStart, up);
    appendQuad(p01, q01, q11, p11, up);
    appendQuad(q01, ridgeStart, ridgeEnd, q11, up);
  } else {
    appendQuad(p00, p10, ridgeEnd, ridgeStart, up);
    appendQuad(p01, ridgeStart, ridgeEnd, p11, up);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}
